/**
 * <style-picker> — Curated framing style preset selector for art PDPs.
 *
 * Self-contained web component that presents filtered/ranked framing presets.
 * Reads LLM classification data from VariantConfiguratorsMetadata and framing
 * options from window.skuPickerInstance. Dispatches the same events as sku-picker
 * so frame mocks, room mocks, price, and URL all update.
 *
 * Does NOT modify sku-picker.js — interacts via window.skuPickerInstance public API.
 */

const BORDER_MAP = { 'full bleed': 'no-border', 'white border': 'border', 'mount': 'mount' };

// Mount width varies by frame size:
//   ≤ 10" max dim → 1" (2.5 cm)
//   ≤ 15" max dim → 1.5" (4 cm)
//   ≥ 16" max dim → 2" (5 cm)
function getMountWidthCm(widthInches, heightInches) {
  const maxDim = Math.max(widthInches || 0, heightInches || 0);
  if (maxDim <= 10) return 2.5;
  if (maxDim <= 15) return 4;
  return 5;
}

function getMountLabel(widthInches, heightInches, unit) {
  const maxDim = Math.max(widthInches || 0, heightInches || 0);
  if (unit === 'cm') {
    if (maxDim <= 10) return '2.5cm';
    if (maxDim <= 15) return '4cm';
    return '5cm';
  }
  if (maxDim <= 10) return '1"';
  if (maxDim <= 15) return '1.5"';
  return '2"';
}

// Keep in sync with default_border_width in blocks/_product-media-gallery.liquid
const WHITE_BORDER_WIDTH_CM = 2;

// Fallback hex colors when color_value is empty (sku-picker often relies on thumbnail images instead)
const COLOR_HEX = {
  black:   '#2d2d2d',
  white:   '#f5f0e8',
  natural: '#c4a574',
  brown:   '#6b4226',
  gold:    '#c9a84c',
  silver:  '#b8b8b8',
};

class StylePicker extends HTMLElement {
  #skuPicker = null;
  #variantMap = {}; // variantId → { artProduct, framingOptions }
  #variantComponentsCache = {}; // variantId → { framing, border }
  #rawPresets = []; // Flat presets from API/Storefront (before resolution)
  #presets = [];
  #allPresets = []; // All resolved presets (before slicing for row display)
  #suggestedColors = [];
  #suggestedBorder = '';
  #activeId = null;
  #preferredStyleId = null; // Stores user's explicit selection
  #stylistPickId = null; // First preset at page load — shown as "Stylist pick"
  #customSourcePreset = null; // allPreset backing the custom card (null = truly custom)
  #sheetDialog = null; // Lazily created <dialog> for "View All" sheet
  #customTabState = null; // { variantId, framing, frameColor, border }
  #activeTab = 'presets'; // 'presets' | 'custom'
  #customPanelBuilt = false; // Whether #buildCustomPanel() has been called
  #customPanelMinHeight = 0; // Tallest measured height of custom panel (mobile)
  #hasUserCustomSelection = false; // Whether user has an active custom/overflow selection to persist in row
  #savedScrollBeforeCustom = null; // Saved body scroll offset when entering custom tab on mobile
  #applyingConfig = false; // Guard to prevent self-triggered event handlers from re-entering
  #externalListenerHandlers = null; // { sizeChanged, selectionChanged, variantChanged }
  #skuPickerObserver = null; // MutationObserver on sku-picker container
  #customAccordionStep = null; // 'material'|'framing'|'color'|'border' or null
  #i18n = {}; // Translations loaded from #style-picker-i18n JSON block

  /** Public accessor so media-gallery can match mock options against resolved presets. */
  get resolvedPresets() {
    return this.#allPresets;
  }

  openAboutDialog() {
    this.#openAboutModal();
  }

  connectedCallback() {
    // Load i18n strings from Liquid-rendered JSON block
    const i18nEl = document.getElementById('style-picker-i18n');
    if (i18nEl) { try { this.#i18n = JSON.parse(i18nEl.textContent); } catch { /* fallback to defaults */ } }

    // Start preset fetch immediately (runs in parallel with sku-picker wait)
    const presetsPromise = this.#loadPresets();
    this.#waitForSkuPicker().then(() => this.#init(presetsPromise));
  }

  disconnectedCallback() {
    if (this.#externalListenerHandlers) {
      document.removeEventListener('size-impact:changed', this.#externalListenerHandlers.sizeChanged);
      document.removeEventListener('sku-picker:selection-changed', this.#externalListenerHandlers.selectionChanged);
      document.removeEventListener('sku-picker:variant-changed', this.#externalListenerHandlers.variantChanged);
      document.removeEventListener('sku-picker:unit-changed', this.#externalListenerHandlers.unitChanged);
      document.removeEventListener('pdp:apply-configuration', this.#externalListenerHandlers.applyConfiguration);
      this.#externalListenerHandlers = null;
    }
    if (this.#skuPickerObserver) {
      this.#skuPickerObserver.disconnect();
      this.#skuPickerObserver = null;
    }
  }

  /** Current frame size in inches (from sku-picker state). */
  #getCurrentSizeDims() {
    const handle = this.#skuPicker?.state?.confirmed?.size_option;
    const opt = this.#skuPicker?.data?.options?.size?.find(s => s.handle === handle);
    return { w: parseFloat(opt?.width_inches) || 16, h: parseFloat(opt?.height_inches) || 20 };
  }

  #mountWidthCm() {
    const { w, h } = this.#getCurrentSizeDims();
    return getMountWidthCm(w, h);
  }

  #borderWidthCm(borderHandle) {
    if (borderHandle === 'mount') return this.#mountWidthCm();
    if (borderHandle === 'border') return WHITE_BORDER_WIDTH_CM;
    return 0;
  }

  #buildCardDescriptor(preset) {
    if (!preset) return '';
    const framing = preset.framing || 'unframed';
    if (framing === 'unframed') {
      if (preset.productType === 'canvas-print') {
        return this.#i18n.descriptor_canvas_unframed || 'Stretched canvas';
      }
      return this.#i18n.descriptor_unframed || 'Unframed · you hang it';
    }

    const v = this.#variantMap[preset.resolvedVariantId];
    const framingOpt = v?.framingOptions.find(f => f.handle === preset.resolvedFraming);
    const color = framingOpt?.colors?.find(c => c.handle === preset.frameColorHandle);

    const colorName = (color?.short_name || color?.label || preset.colorShort || preset.colorFamily || '').trim();
    const niceColor = colorName
      ? (colorName.charAt(0).toUpperCase() + colorName.slice(1).toLowerCase())
      : '';

    if (preset.productType === 'canvas-print') {
      return niceColor ? `${niceColor} Frame` : (this.#i18n.descriptor_framed_default || 'Framed');
    }

    const fallbackFrame = this.#i18n.descriptor_framed_default || 'Framed';
    const frameName = (framingOpt?.label || framingOpt?.short_name || framingOpt?.name || '').trim();
    const colorAndFrame = [niceColor, frameName].filter(Boolean).join(' ') || fallbackFrame;

    if (!preset.border || preset.border === 'no-border') return colorAndFrame;

    const variantConfig = this.#skuPicker?.variantConfigurators?.variants?.[preset.resolvedVariantId];
    const borderOpt = variantConfig?.options?.border?.find(b => b.handle === preset.border);
    const borderLabel = borderOpt?.label || '';
    return borderLabel ? `${colorAndFrame} · ${borderLabel}` : colorAndFrame;
  }

  /**
   * Compute the formatted total price for a preset at the current size.
   * Falls back to '' if pricing data is missing for the size, so the price line
   * just collapses rather than rendering "undefined". Mirrors hifi `.sc-price`.
   */
  #buildCardPrice(preset) {
    if (!preset || !this.#skuPicker) return '';
    const sizeHandle = this.#skuPicker.state?.confirmed?.size_option;
    if (!sizeHandle) return '';
    const framingHandle = preset.resolvedFraming || preset.framing || 'unframed';
    if (framingHandle === 'unframed') return '';
    const components = this.#getVariantComponents(preset.resolvedVariantId);
    const framingComp = components.framing?.[sizeHandle]?.[framingHandle];
    const borderHandle = preset.border || 'no-border';
    const borderComp = components.border?.[sizeHandle]?.[borderHandle];
    // Canvas configurators have no border pricing (canvas never has a border); treat missing no-border as free.
    if (!framingComp) return '';
    if (!borderComp && borderHandle !== 'no-border') return '';
    const delta = (framingComp.price || 0) + (borderComp?.price || 0);
    if (delta <= 0) return '';
    return `+${this.#skuPicker._formatPrice(delta)}`;
  }

  async #waitForSkuPicker() {
    // Fast path: already initialized
    if (window.skuPickerInstance?.data?.options?.framing) return;
    // Wait for sku-picker:ready event instead of polling
    await new Promise(r => {
      const handler = () => {
        if (window.skuPickerInstance?.data?.options?.framing) {
          window.removeEventListener('sku-picker:ready', handler);
          r();
        }
      };
      window.addEventListener('sku-picker:ready', handler);
      // Guard: if it became ready between the check and the listener
      if (window.skuPickerInstance?.data?.options?.framing) {
        window.removeEventListener('sku-picker:ready', handler);
        r();
      }
    });
  }

  async #init(presetsPromise) {
    this.#skuPicker = window.skuPickerInstance;
    this.#initAllVariants();

    // Don't render if no framing options across all variants
    if (!Object.values(this.#variantMap).some(v => v.framingOptions.length)) return;

    this.#rawPresets = await presetsPromise;
    if (!this.#rawPresets.length) return;

    this.#readLlmSignals();
    this.#resolvePresets();

    // Store the first preset as the "Stylist pick" — persists across re-sorts
    if (!this.#stylistPickId && this.#presets.length) {
      this.#stylistPickId = this.#presets[0].id;
    }

    // Don't render if no presets available
    if (!this.#presets.length) return;

    this.#loadCSS();

    // Register a pure resolver on the sku-picker so the cart "Style" line is
    // derived from state.confirmed at form-build time. This makes the style
    // name immune to the stale/premature/racing failure modes of pushing
    // `activeStyleName` from many separate handlers.
    this.#skuPicker.resolveStyleName = (confirmed) => this.#resolveStyleName(confirmed);

    const presetMatched = this.#detectActivePreset();
    // Apply the active preset on init so mocks show the correct frame config.
    // If URL params already drove the sku-picker state, skip state changes but
    // still broadcast config so frame/room mocks render the correct framing.
    const hasUrlConfig = new URLSearchParams(window.location.search).has('framing')
      || !!window.__pdpInboundSkuParams?.framing;
    delete window.__pdpInboundSkuParams; // consumed by sku-picker + style-picker
    const activePreset = this.#presets.find(p => p.id === this.#activeId)
      || this.#allPresets.find(p => p.id === this.#activeId);
    if (activePreset) this.#applyPresetConfig(activePreset, { isInit: true, skipSkuPickerState: hasUrlConfig });

    this.#render();
    // If URL params specify a config that doesn't match any preset, show Custom card
    if (hasUrlConfig && !presetMatched) {
      this.#hasUserCustomSelection = true;
      this.#showCustomCard();
    // If the detected preset is an overflow (not in row), show it as the custom card
    } else if (activePreset && !this.#presets.includes(activePreset)) {
      this.#hasUserCustomSelection = true;
      this.#showCustomCard(activePreset);
    }
    this.#hideSkuPickerRows();
    this.#listenForExternalChanges();

    this.dispatchEvent(new CustomEvent('style-picker:ready', { bubbles: true }));
  }

  /* ── Preset loading (API → Storefront API fallback) ── */

  async #loadPresets() {
    const skipCache = new URLSearchParams(window.location.search).get('_skuCache') === 'direct';
    const isDefaultLocale = this.#getLanguageCode() === 'EN';

    // 1. Try inline SSR data (zero network calls) — English only (shop metafield is not locale-aware)
    if (!skipCache && isDefaultLocale) {
      try {
        const el = document.getElementById('StylePresetsData');
        if (el) {
          const data = JSON.parse(el.textContent);
          if (Array.isArray(data) && data.length) return data;
        }
      } catch { /* invalid JSON — fall through */ }
    }

    // 2. Try Railway cache API — English only (cache is not locale-aware)
    if (!skipCache && isDefaultLocale) {
      try {
        const res = await fetch('https://shop-chat-agent.iamfy.tech/api/style-presets');
        if (res.ok) {
          const data = await res.json();
          if (Array.isArray(data) && data.length) return data;
        }
      } catch { /* fall through to Storefront API */ }
    }

    // 3. Storefront API (uses @inContext for locale-aware translations)
    return this.#fetchPresetsViaStorefrontAPI();
  }

  /** Get Shopify LanguageCode (e.g. "DE", "FR") from locale or HTML lang. */
  #getLanguageCode() {
    const locale = window.Shopify?.locale || document.documentElement.lang || 'en';
    return locale.split('-')[0].toUpperCase();
  }

  async #fetchPresetsViaStorefrontAPI() {
    const token = this.#getStorefrontAccessToken();
    if (!token) return [];

    const shop = window.Shopify?.shop || window.location.hostname;
    const language = this.#getLanguageCode();
    const query = `query($language: LanguageCode) @inContext(language: $language) {
      metaobjects(type: "style_preset", first: 50) {
        nodes {
          handle
          name: field(key: "name") { value }
          description: field(key: "description") { value }
          vibe: field(key: "vibe") { value }
          productType: field(key: "product_type") {
            reference { ... on Metaobject { handle } }
          }
          framing: field(key: "framing") {
            reference { ... on Metaobject { handle } }
          }
          frameColor: field(key: "frame_color") {
            reference {
              ... on Metaobject {
                handle
                shortName: field(key: "short_name") { value }
              }
            }
          }
          border: field(key: "border") {
            reference { ... on Metaobject { handle } }
          }
          curatorNotes: field(key: "curator_notes") { value }
          galleryImages: field(key: "gallery_images") {
            references(first: 10) {
              nodes {
                ... on MediaImage {
                  image {
                    url(transform: { maxWidth: 800 })
                    altText
                    width
                    height
                  }
                }
              }
            }
          }
        }
      }
    }`;

    try {
      const res = await fetch(`https://${shop}/api/2025-07/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': token,
        },
        body: JSON.stringify({ query, variables: { language } }),
      });
      if (!res.ok) return [];
      const json = await res.json();
      const nodes = json.data?.metaobjects?.nodes || [];
      return nodes.map(n => {
        const shortName = n.frameColor?.reference?.shortName?.value || null;
        return {
          id: n.handle,
          name: n.name?.value || '',
          description: n.description?.value || '',
          vibe: n.vibe?.value || 'modern',
          productType: n.productType?.reference?.handle || 'art-print',
          framing: n.framing?.reference?.handle || 'unframed',
          colorShort: shortName,
          colorFamily: shortName ? shortName.toLowerCase() : null,
          border: n.border?.reference?.handle || 'no-border',
          curatorNotes: n.curatorNotes?.value || '',
          galleryImages: (n.galleryImages?.references?.nodes || [])
            .map(img => ({
              url: img.image?.url,
              alt: img.image?.altText || '',
              width: img.image?.width,
              height: img.image?.height,
            }))
            .filter(img => img.url),
        };
      });
    } catch { return []; }
  }

  #getStorefrontAccessToken() {
    const metaTag = document.querySelector('meta[name="shopify-storefront-api-token"]');
    if (metaTag?.content) return metaTag.content;

    const tokenElement = document.querySelector('[data-storefront-access-token]');
    if (tokenElement?.dataset.storefrontAccessToken) return tokenElement.dataset.storefrontAccessToken;

    if (window.Shopify?.storefrontAccessToken) return window.Shopify.storefrontAccessToken;

    return null;
  }

  /* ── Cross-variant init ── */

  #initAllVariants() {
    const variants = this.#skuPicker.variantConfigurators.variants;
    for (const [vid, config] of Object.entries(variants)) {
      this.#variantMap[vid] = {
        artProduct: config.artProduct,
        framingOptions: config.options?.framing || [],
      };
    }
  }

  /** Build a { size, framing, border } components map for a given variant (cached).
   *  Each leaf is { price, compare_at_price } — truthy for availability checks and
   *  usable for pricing so the custom tab doesn't depend on the sku-picker's loaded variant. */
  #getVariantComponents(variantId) {
    if (this.#variantComponentsCache[variantId]) return this.#variantComponentsCache[variantId];
    const config = this.#skuPicker.variantConfigurators.variants[variantId];
    const parse = v => { const n = parseFloat(v); return isNaN(n) ? 0 : n; };
    const fmt = p => this.#skuPicker._formatPrice(p);
    const size = {};
    const framing = {};
    const border = {};
    if (config?.size_pricing) {
      for (const item of config.size_pricing) {
        if (!item.size_option) continue;
        const p = parse(item.price), cp = parse(item.compare_at_price);
        size[item.size_option] = { price: p, compare_at_price: cp, price_formatted: fmt(p), compare_at_price_formatted: cp > 0 ? fmt(cp) : null };
      }
    }
    if (config?.framing_pricing) {
      for (const item of config.framing_pricing) {
        if (!item.size_option || !item.framing_option) continue;
        const p = parse(item.price), cp = parse(item.compare_at_price);
        (framing[item.size_option] ??= {})[item.framing_option] = { price: p, compare_at_price: cp, price_formatted: fmt(p), compare_at_price_formatted: cp > 0 ? fmt(cp) : null };
      }
    }
    if (config?.border_pricing) {
      for (const item of config.border_pricing) {
        if (!item.size_option || !item.border_option) continue;
        const p = parse(item.price), cp = parse(item.compare_at_price);
        (border[item.size_option] ??= {})[item.border_option] = { price: p, compare_at_price: cp, price_formatted: fmt(p), compare_at_price_formatted: cp > 0 ? fmt(cp) : null };
      }
    }
    const result = { size, framing, border };
    this.#variantComponentsCache[variantId] = result;
    return result;
  }

  /* ── LLM signals ── */

  #readLlmSignals() {
    const metadata = document.getElementById('VariantConfiguratorsMetadata');
    if (metadata) {
      try {
        const data = JSON.parse(metadata.textContent);
        this.#suggestedColors = data.suggestedFrameColors || [];
        this.#suggestedBorder = data.suggestedBorder || '';
      } catch { /* ignore */ }
    }
  }

  /* ── Preset resolution & ranking ── */

  /**
   * Find the actual framing option that matches a preset's abstract framing key.
   * Tries exact match first, then suffix match (e.g. 'classic-frame' matches
   * 'canvas-classic-frame') to handle product-type-specific prefixes.
   */
  #findFramingOption(framingOptions, abstractHandle) {
    return framingOptions.find(f => f.handle === abstractHandle)
      || framingOptions.find(f => f.handle.endsWith('-' + abstractHandle));
  }

  #resolvePresets() {
    const currentVid = this.#skuPicker.currentVariantId;
    const allVids = Object.keys(this.#variantMap);

    this.#presets = this.#rawPresets
      .map(p => {
        // Resolve against variants matching the preset's productType first,
        // then fall back to any variant. Within each group prefer currentVid.
        const sorted = [...allVids].sort((a, b) => {
          const aMatch = this.#variantMap[a].artProduct === p.productType ? 0 : 1;
          const bMatch = this.#variantMap[b].artProduct === p.productType ? 0 : 1;
          if (aMatch !== bMatch) return aMatch - bMatch;
          if (a === currentVid) return -1;
          if (b === currentVid) return 1;
          return 0;
        });

        for (const vid of sorted) {
          const v = this.#variantMap[vid];
          const framingOpt = this.#findFramingOption(v.framingOptions, p.framing);
          if (!framingOpt) continue;
          const color = framingOpt.colors?.find(c => c.short_name === p.colorShort);
          if (p.framing !== 'unframed' && !color) continue;
          return {
            ...p,
            resolvedFraming: framingOpt.handle,
            framingShortName: framingOpt.short_name || null,
            resolvedVariantId: vid,
            frameColorHandle: color?.handle || null,
            hex: color?.color_value || COLOR_HEX[p.colorFamily] || '#333',
            thumbnail: color?.thumbnail || null,
            mouldingTile: color?.moulding_tile || null,
            mouldingWidth: color?.moulding_width || null,
          };
        }
        return null;
      })
      .filter(Boolean);

    // Score & rank
    this.#presets.sort((a, b) => this.#scorePreset(b) - this.#scorePreset(a));

    // Store all resolved presets before slicing (for "View All" sheet)
    this.#allPresets = [...this.#presets];

    // Top 4 available for the row; remaining accessible via "View All" sheet
    this.#pickRowPresets();
  }

  #scorePreset(preset) {
    if (preset.framing === 'unframed') return -1;

    // Fallback: no LLM data → use deterministic order from presets
    const hasLlm = this.#suggestedColors.length > 0 || this.#suggestedBorder !== '';
    if (!hasLlm) return 0;

    let score = 0;
    if (preset.colorFamily && this.#suggestedColors.includes(preset.colorFamily)) score += 2;
    if (BORDER_MAP[this.#suggestedBorder] === preset.border) score += 1;
    return score;
  }

  /**
   * Pick the top 4 available presets for the row (re-evaluated on every size change).
   * If the user has a #preferredStyleId that's available, ensure it's included.
   */
  #pickRowPresets() {
    const available = this.#allPresets.filter(p => this.#isPresetAvailable(p));
    available.sort((a, b) => this.#scorePreset(b) - this.#scorePreset(a));

    const framed = available.filter(p => p.framing !== 'unframed');
    const currentVid = this.#skuPicker.currentVariantId;
    const unframed = available.find(p => p.framing === 'unframed' && p.id === 'unframed' && p.resolvedVariantId === currentVid)
      || available.find(p => p.framing === 'unframed' && p.id === 'unframed')
      || available.find(p => p.framing === 'unframed' && p.resolvedVariantId === currentVid)
      || available.find(p => p.framing === 'unframed');

    let picked = framed.slice(0, 3);

    if (this.#preferredStyleId && !picked.some(p => p.id === this.#preferredStyleId)) {
      const preferred = framed.find(p => p.id === this.#preferredStyleId);
      if (preferred && picked.length === 3) {
        picked[2] = preferred;
      } else if (preferred) {
        picked.push(preferred);
      }
    }

    if (unframed) picked.push(unframed);

    this.#presets = picked.slice(0, 4);
  }

  /**
   * Patch the row card DOM nodes to match the current #presets (in-place, no DOM rebuild).
   * Hides surplus slots when fewer than 4 presets are available.
   */
  #updateRowCards() {
    const cards = [...this.querySelectorAll('.style-picker__card')]
      .filter(c => c.dataset.styleId !== 'custom');

    // When custom card is visible, limit preset cards to 3 so total stays at 4
    const customCard = this.querySelector('[data-style-id="custom"]');
    const customVisible = customCard && !customCard.classList.contains('style-picker__card--hidden');
    const maxVisible = customVisible ? 3 : 4;

    cards.forEach((card, i) => {
      const preset = this.#presets[i];
      if (!preset || i >= maxVisible) {
        card.style.display = 'none';
        return;
      }

      card.style.display = '';
      card.dataset.styleId = preset.id;
      card.dataset.productType = preset.productType === 'canvas-print' ? 'canvas' : 'art_print';
      card.classList.toggle('style-picker__card--active', preset.id === this.#activeId);
      card.classList.remove('style-picker__card--unavailable');

      const nameEl = card.querySelector('.style-picker__name');
      if (nameEl) nameEl.textContent = preset.name;

      const previewEl = card.querySelector('.style-picker__preview');
      let stylistBadgeEl = card.querySelector('.style-picker__stylist-pick');
      const shouldShowPick = preset.id === this.#stylistPickId;
      if (shouldShowPick && !stylistBadgeEl && previewEl) {
        stylistBadgeEl = document.createElement('span');
        stylistBadgeEl.className = 'style-picker__stylist-pick';
        stylistBadgeEl.textContent = this.#i18n.stylist_pick_short || 'Pick';
        previewEl.insertBefore(stylistBadgeEl, previewEl.firstChild);
      } else if (!shouldShowPick && stylistBadgeEl) {
        stylistBadgeEl.remove();
      }

      const textWrap = card.querySelector('.style-picker__text');
      const descriptorEl = card.querySelector('.style-picker__descriptor');
      const descriptorText = this.#buildCardDescriptor(preset);
      if (descriptorEl) {
        descriptorEl.textContent = descriptorText || '';
      } else if (textWrap) {
        const desc = document.createElement('span');
        desc.className = 'style-picker__descriptor';
        desc.textContent = descriptorText || '';
        const priceEl = textWrap.querySelector('.style-picker__price');
        textWrap.insertBefore(desc, priceEl || null);
      }

      const priceText = this.#buildCardPrice(preset);
      let priceEl = card.querySelector('.style-picker__price');
      if (priceText) {
        if (priceEl) {
          priceEl.textContent = priceText;
          priceEl.style.display = '';
        } else if (textWrap) {
          priceEl = document.createElement('span');
          priceEl.className = 'style-picker__price';
          priceEl.textContent = priceText;
          textWrap.appendChild(priceEl);
        }
      } else if (priceEl) {
        priceEl.style.display = 'none';
      }

      const mock = card.querySelector('gallery-wall-frame-mock');
      if (mock) {
        const isUnframed = preset.framing === 'unframed';
        const artProduct = this.#variantMap[preset.resolvedVariantId]?.artProduct;
        const productType = artProduct === 'canvas-print' ? 'canvas' : 'art_print';
        mock.updateConfig({
          frameColor: preset.hex || '',
          mouldingTile: isUnframed ? '' : (preset.mouldingTile || ''),
          mouldingWidth: isUnframed ? 0 : (preset.mouldingWidth || null),
          borderWidth: this.#borderWidthCm(preset.border),
          borderOption: preset.border,
          hasFrame: !isUnframed,
          productType,
        });
      }
    });
  }

  /* ── Preset availability (size-dependent) ── */

  #isPresetAvailable(preset) {
    const currentSize = this.#skuPicker.state.confirmed.size_option;
    const isUnframed = preset.framing === 'unframed';

    // Pricing checks can be skipped for unframed (always priced at 0),
    // but framed presets must verify pricing data exists for this size.
    if (!isUnframed) {
      const isCurrentVariant = preset.resolvedVariantId === this.#skuPicker.currentVariantId;

      if (isCurrentVariant) {
        // Fast path: use pre-built components map
        if (!this.#skuPicker.components.framing[currentSize]?.[preset.resolvedFraming]) return false;
        if (preset.border === 'mount' && !this.#skuPicker.components.border[currentSize]?.['mount']) return false;
      } else {
        // Cross-variant: check the variant's raw pricing data directly
        const variantData = this.#skuPicker.variantConfigurators?.variants?.[preset.resolvedVariantId];
        if (!variantData) return false;

        const framingPricing = variantData.framing_pricing || [];
        const hasFraming = framingPricing.some(
          item => item.size_option === currentSize && item.framing_option === preset.resolvedFraming
        );
        if (!hasFraming) return false;

        if (preset.border === 'mount') {
          const borderPricing = variantData.border_pricing || [];
          const hasBorder = borderPricing.some(
            item => item.size_option === currentSize && item.border_option === 'mount'
          );
          if (!hasBorder) return false;
        }
      }
    }

    // Validate that a fulfillment SKU exists for this combination.
    // Prevents showing presets as available when no Prodigi config exists
    // (e.g. Bold Canvas in 9x12, or unframed canvas in 9x12).
    const artProduct = this.#variantMap[preset.resolvedVariantId]?.artProduct || null;
    const prodigiConfig = this.#skuPicker._lookupProdigiConfig(
      currentSize, preset.resolvedFraming, preset.border, artProduct
    );
    if (!prodigiConfig.prodigi_sku) return false;

    return true;
  }

  /** Check whether the current custom tab config is valid for the current size. */
  #isCustomTabAvailable() {
    const state = this.#customTabState;
    if (!state) return false;
    const currentSize = this.#skuPicker.state.confirmed.size_option;
    const vc = this.#getVariantComponents(state.variantId);
    const isUnframed = state.framing === 'unframed' || state.framing?.endsWith('-unframed');
    if (!isUnframed && !vc.framing[currentSize]?.[state.framing]) return false;
    if (state.border !== 'no-border' && !vc.border[currentSize]?.[state.border]) return false;

    // Validate fulfillment SKU exists
    if (!isUnframed) {
      const artProduct = this.#variantMap[state.variantId]?.artProduct || null;
      const prodigiConfig = this.#skuPicker._lookupProdigiConfig(
        currentSize, state.framing, state.border || 'no-border', artProduct
      );
      if (!prodigiConfig.prodigi_sku) return false;
    }

    return true;
  }

  /* ── Detect which preset matches current sku-picker state ── */

  #detectActivePreset() {
    const sel = this.#skuPicker.state.confirmed;
    const vid = this.#skuPicker.currentVariantId;
    const matcher = p =>
      p.resolvedVariantId === vid &&
      p.resolvedFraming === sel.framing_option &&
      p.frameColorHandle === sel.frame_color &&
      p.border === sel.border_option;
    const match = this.#presets.find(matcher)
      || this.#allPresets.find(matcher);
    this.#activeId = match?.id || (this.#presets[0]?.id || null);
    // Treat the initial match as the user's preferred style so that
    // #handleSizeChange can revert to it after a size-driven fallback.
    // This matters when landing via a URL with explicit framing params.
    if (match) this.#preferredStyleId = match.id;
    return !!match;
  }

  /**
   * Resolve the branded style name for a confirmed sku-picker selection.
   * Registered on the sku-picker as `resolveStyleName` so that
   * _updateFormProperties derives the cart "Style" line purely from
   * state.confirmed — it can never go stale, because it is recomputed at
   * form-build time rather than pushed from whichever handler ran last.
   */
  #resolveStyleName(confirmed) {
    if (!confirmed) return null;
    const vid = this.#skuPicker.currentVariantId;
    const match = this.#allPresets.find(p =>
      p.resolvedVariantId === vid &&
      p.resolvedFraming === confirmed.framing_option &&
      (p.frameColorHandle || null) === (confirmed.frame_color || null) &&
      (p.border || 'no-border') === (confirmed.border_option || 'no-border')
    );
    return match ? (match.name || null) : null;
  }

  /**
   * Whether a (framing, border) combination is fulfillable for the current
   * size. Border validity is framing-dependent — a deep box frame supports a
   * mount, a flat classic frame does not — so this checks the actual Prodigi
   * fulfillment combo rather than variant/size-level border pricing.
   */
  #isBorderValidForFraming(variantId, framing, border) {
    if (!border || border === 'no-border') return true;
    const isUnframed = framing === 'unframed' || framing?.endsWith('-unframed');
    if (isUnframed) return false; // unframed never carries a border/mount
    const currentSize = this.#skuPicker.state.confirmed.size_option;
    const artProduct = this.#variantMap[variantId]?.artProduct || null;
    const cfg = this.#skuPicker._lookupProdigiConfig(currentSize, framing, border, artProduct);
    return !!cfg.prodigi_sku;
  }

  /* ── CSS loading ── */

  #loadCSS() {
    if (document.getElementById('style-picker-styles')) return;
    const link = document.createElement('link');
    link.id = 'style-picker-styles';
    link.rel = 'stylesheet';
    link.href = this.#assetUrl('style-picker.css');
    document.head.appendChild(link);
  }

  #assetUrl(filename) {
    const existing = document.querySelector('link[href*="/assets/"], script[src*="/assets/"]');
    if (existing) {
      const url = existing.href || existing.src;
      return url.replace(/\/assets\/[^?]+/, `/assets/${filename}`);
    }
    return `/assets/${filename}`;
  }

  /* ── Hide sku-picker framing/border rows ── */

  #hideSkuPickerRows() {
    const container = this.#skuPicker.container;
    if (!container) return;
    const hasSizeSlider = this.getAttribute('data-has-size-slider') !== 'false';
    container.querySelectorAll('.option-row').forEach(row => {
      // Keep the size row visible: either it contains the slider, or slider is off (show dropdown)
      if (row.querySelector('size-impact-slider') || (!hasSizeSlider && row.querySelector('[data-option-name="size"]'))) {
        return;
      }
      row.style.display = 'none';
    });
  }

  /* ── DOM rendering ── */

  #render() {
    // Read shared asset URLs from own Liquid-rendered data attributes
    const sharedAttrs = {
      artworkUrl: this.dataset.artworkUrl || '',
      artworkAspect: this.dataset.artworkAspect || '1',
      wallTexture: this.dataset.wallTexture || '',
      roomLightingPortrait: this.dataset.roomLightingPortrait || '',
      roomLightingLandscape: this.dataset.roomLightingLandscape || '',
      roomLightingSquare: this.dataset.roomLightingSquare || '',
      mouldingTile: '',  // per-preset from resolved data
      mouldingWidth: '15',  // per-preset from resolved data
    };

    // Headless on the surgical PDP: pdp-surgical-config.js renders the visible
    // cards (CSS). This legacy <style-picker> is display:none there and only
    // provides preset data + the About dialog — so skip its canvas card
    // previews entirely. (display:none canvas elements still composite on the
    // main thread, so leaving them would defeat the perf goal.)
    const headless = !!document.querySelector('pdp-surgical-frame-picker');

    const pickLabel = this.#i18n.stylist_pick_short || 'Pick';

    const cardsHTML = this.#presets.map(p => {
      const active = p.id === this.#activeId ? ' style-picker__card--active' : '';
      const unavailable = !this.#isPresetAvailable(p) ? ' style-picker__card--unavailable' : '';

      const artProduct = this.#variantMap[p.resolvedVariantId]?.artProduct;
      const productType = artProduct === 'canvas-print' ? 'canvas' : 'art_print';
      const isUnframed = p.framing === 'unframed';
      const previewContent = headless ? '' : `<gallery-wall-frame-mock class="style-picker__frame-preview"
          data-corner-crop="true"
          data-artwork-url="${sharedAttrs.artworkUrl}"
          data-artwork-aspect="${sharedAttrs.artworkAspect}"
          data-wall-texture="${sharedAttrs.wallTexture}"
          data-room-lighting-portrait="${sharedAttrs.roomLightingPortrait}"
          data-room-lighting-landscape="${sharedAttrs.roomLightingLandscape}"
          data-room-lighting-square="${sharedAttrs.roomLightingSquare}"
          data-moulding-tile="${isUnframed ? '' : (p.mouldingTile || sharedAttrs.mouldingTile)}"
          data-moulding-width="${isUnframed ? '0' : (p.mouldingWidth || sharedAttrs.mouldingWidth)}"
          data-frame-color="${p.hex || ''}"
          data-border-width="${this.#borderWidthCm(p.border)}"
          data-border-option="${p.border}"
          data-has-frame="${!isUnframed}"
          data-product-type="${productType}"
        ><canvas></canvas></gallery-wall-frame-mock>`;

      const stylistBadge = p.id === this.#stylistPickId ? `<span class="style-picker__stylist-pick">${pickLabel}</span>` : '';
      const descriptor = this.#buildCardDescriptor(p);
      const descriptorHTML = `<span class="style-picker__descriptor">${descriptor || ''}</span>`;
      const priceText = this.#buildCardPrice(p);
      const priceHTML = priceText ? `<span class="style-picker__price">${priceText}</span>` : '';
      return `<button class="style-picker__card${active}${unavailable}" data-style-id="${p.id}" data-product-type="${productType}" type="button">
        <div class="style-picker__preview">${previewContent}${stylistBadge}<span class="style-picker__check" aria-hidden="true">✓</span></div>
        <div class="style-picker__text">
          <span class="style-picker__name">${p.name}</span>
          ${descriptorHTML}
          ${priceHTML}
        </div>
      </button>`;
    }).join('');

    const customCardHTML = `<button class="style-picker__card style-picker__card--hidden" data-style-id="custom" type="button">
      <div class="style-picker__preview">${headless ? '' : `<gallery-wall-frame-mock class="style-picker__frame-preview"
          data-corner-crop="true"
          data-artwork-url="${sharedAttrs.artworkUrl}"
          data-artwork-aspect="${sharedAttrs.artworkAspect}"
          data-wall-texture="${sharedAttrs.wallTexture}"
          data-room-lighting-portrait="${sharedAttrs.roomLightingPortrait}"
          data-room-lighting-landscape="${sharedAttrs.roomLightingLandscape}"
          data-room-lighting-square="${sharedAttrs.roomLightingSquare}"
          data-moulding-tile="${sharedAttrs.mouldingTile}"
          data-moulding-width="${sharedAttrs.mouldingWidth}"
          data-frame-color="#333"
          data-border-width="0"
          data-border-option="no-border"
          data-has-frame="true"
          data-product-type="art_print"
        ><canvas></canvas></gallery-wall-frame-mock>`}<span class="style-picker__check" aria-hidden="true">✓</span></div>
      <div class="style-picker__text">
        <span class="style-picker__name">${this.#i18n.custom || 'Custom'}</span>
        <span class="style-picker__descriptor"></span>
      </div>
    </button>`;

    const viewAllLabel = this.#i18n.view_all || 'View all frames';
    const buildLabel = this.#i18n.build_your_own || 'Build your own frame.';

    this.innerHTML = `
      <div class="style-picker">
        <div class="style-picker__header">
          <div class="style-picker__header-title">${this.#i18n.style_label || 'Framing'}: <span class="style-picker__header-value">${this.#getPresetName(this.#activeId)}</span>${this.#buildAboutButton(this.#activeId)}</div>
          <div class="style-picker__header-description">${this.#getPresetDescription(this.#activeId)}</div>
        </div>
        <div class="style-picker__grid">${customCardHTML}${cardsHTML}</div>
        <div class="style-picker__ctas">
          <button class="style-picker__view-all-link" type="button">${viewAllLabel}</button>
          <button class="style-picker__custom-link" type="button">${buildLabel}</button>
        </div>
      </div>
    `;

    // Event listeners
    this.querySelectorAll('.style-picker__card').forEach(card => {
      card.addEventListener('click', () => {
        const preset = this.#presets.find(p => p.id === card.dataset.styleId);
        if (!preset || !this.#isPresetAvailable(preset)) return; // Ignore disabled clicks

        this.#preferredStyleId = preset.id; // Remember user's preference
        this.#handleCardClick(preset);

        // Only auto-switch if on a lifestyle/info slide (not frame or room)
        this.#switchFromLifestyleSlide();
      });
    });

    // "View all frames" link opens the all-styles sheet
    const viewAllLink = this.querySelector('.style-picker__view-all-link');
    if (viewAllLink) {
      viewAllLink.addEventListener('click', () => this.#openSheet());
    }

    this.querySelector('.style-picker')?.addEventListener('click', (e) => {
      if (e.target.closest('[data-about-style]')) {
        this.#openAboutModal();
      }
    });

    this.querySelector('.style-picker__custom-link').addEventListener('click', () => {
      this.#openSheet('custom');
    });

    this.querySelector('[data-style-id="custom"]').addEventListener('click', () => {
      if (this.#customSourcePreset) {
        this.#applyPresetConfig(this.#customSourcePreset);
        this.#setActiveCard('custom');
        this.#switchFromLifestyleSlide();
      } else if (this.#customTabState) {
        // Re-apply the saved custom config directly (no need to reopen the sheet)
        this.#applyCustomConfig();
        this.#setActiveCard('custom');
      } else {
        this.#openSheet('custom');
      }
    });

  }

  /* ── Slide transitions ── */

  /**
   * Smoothly transition from room mock (slide 1) to frame mock (slide 0).
   * Clones the room mock's visual container (plain HTML — no custom element
   * callbacks) as an overlay, instantly switches slides underneath, then
   * fades the overlay out.
   */
  #switchToFrameSlide() {
    const mediaGallery = document.querySelector('media-gallery') || document.querySelector('gallery-wall-media-gallery');
    const slideshow = mediaGallery?.slideshow;
    if (!slideshow) return;
    if (slideshow.current !== 1) {
      slideshow.select(0);
      return;
    }

    // Clone the room mock's visual container (plain <div>/<img>/<svg>)
    const currentSlide = slideshow.slides?.[1];
    const container = currentSlide?.querySelector('.room-background-mock__container');
    if (!container) {
      slideshow.select(0);
      return;
    }

    const slideshowEl = slideshow.refs?.slideshowContainer || slideshow;
    const slideRect = currentSlide.getBoundingClientRect();
    const parentRect = slideshowEl.getBoundingClientRect();

    const overlay = document.createElement('div');
    overlay.className = 'frame-room-transition-overlay';
    overlay.style.cssText = `
      position:absolute; z-index:10; pointer-events:none;
      top:${slideRect.top - parentRect.top}px;
      left:${slideRect.left - parentRect.left}px;
      width:${slideRect.width}px;
      height:${slideRect.height}px;
      opacity:1;
      transition:opacity 350ms ease-out;
      will-change:opacity;
      overflow:hidden;
    `;

    overlay.appendChild(container.cloneNode(true));

    const prevPos = slideshowEl.style.position;
    if (!prevPos || prevPos === 'static') slideshowEl.style.position = 'relative';

    slideshowEl.appendChild(overlay);

    // Switch slide immediately — cloned DOM is already painted
    slideshow.select(0, undefined, { animate: false });

    // Double-rAF: let frame mock paint, then fade overlay out
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        overlay.style.opacity = '0';

        const cleanup = () => {
          overlay.remove();
          if (prevPos) slideshowEl.style.position = prevPos;
        };
        overlay.addEventListener('transitionend', cleanup, { once: true });
        setTimeout(cleanup, 400); // Safety fallback
      });
    });
  }

  /**
   * Switch to frame slide only if currently on a lifestyle/info slide (index > 1).
   * Slides 0 (frame) and 1 (room) already show style changes live.
   */
  #switchFromLifestyleSlide() {
    const mediaGallery = document.querySelector('media-gallery');
    const slideshow = mediaGallery?.slideshow;
    if (slideshow && slideshow.current > 1) {
      this.#switchToFrameSlide();
    }
  }

  /* ── Card click handler ── */

  #handleCardClick(preset) {
    this.#setActiveCard(preset.id);
    this.#applyPresetConfig(preset);

    document.dispatchEvent(new CustomEvent('style-picker:preset-selected', {
      detail: {
        presetId: preset.id,
        presetName: preset.name,
        framing: preset.resolvedFraming,
        frameColor: preset.frameColorHandle,
        border: preset.border,
        source: 'row',
      }
    }));
  }

  /** Apply a preset's config to sku-picker and dispatch events (without touching active card UI). */
  #applyPresetConfig(preset, { isInit = false, skipSkuPickerState = false } = {}) {
    this.#applyingConfig = true;
    const sp = this.#skuPicker;
    let needsVariantSwitch = false;

    // Tell sku-picker the active style name so _updateFormProperties uses
    // the combined "Style" format. Named presets get their name; the caller
    // is responsible for setting null when truly custom (see #showCustomCard).
    sp.activeStyleName = preset.name || null;

    if (!skipSkuPickerState) {
      needsVariantSwitch = preset.resolvedVariantId !== sp.currentVariantId;

      if (needsVariantSwitch) {
        // Load the target variant's configurator (swaps sp.data, re-renders rows)
        sp.currentVariantId = preset.resolvedVariantId;
        sp._loadVariantConfigurator(preset.resolvedVariantId);

        // Notify media gallery (it loads new gallery data & mocks per variant).
        // We bypass _handleVariantChange (to avoid its rAF debounce), so must emit manually.
        // Include `productType` in the detail so listeners (quality chips,
        // learn-more-tabs) can branch without querying global state — required
        // because section-morph is suppressed for in-row style-picker switches.
        const spArtProduct = sp.variantConfigurators?.variants?.[preset.resolvedVariantId]?.artProduct;
        const spProductType = spArtProduct === 'canvas-print' ? 'canvas' : 'art_print';
        sp.container.dispatchEvent(new CustomEvent('sku-picker:variant-changed', {
          bubbles: true,
          detail: { variantId: preset.resolvedVariantId, productType: spProductType },
        }));

        const radio = document.querySelector(
          `variant-picker input[data-variant-id="${preset.resolvedVariantId}"]`
        );
        if (radio && !radio.checked) {
          radio.checked = true;
        }
        try {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', preset.resolvedVariantId);
          history.replaceState({}, '', url.toString());
        } catch (e) { /* ignore — URL sync is best-effort */ }

        // Re-hide sku-picker rows (re-created by _loadVariantConfigurator)
        this.#hideSkuPickerRows();
      }

      // Update sku-picker state using resolved (actual) framing handle
      sp.state.confirmed.framing_option = preset.resolvedFraming;
      sp.state.confirmed.frame_color = preset.frameColorHandle;
      sp.state.confirmed.border_option = preset.border;

      // Enforce constraint: mount requires frame
      if (preset.resolvedFraming === 'unframed' && sp.state.confirmed.border_option === 'mount') {
        sp.state.confirmed.border_option = 'no-border';
      }

      sp._updateConfirmedSelection(true, { skipUrl: skipSkuPickerState });
    } else {
      // skipSkuPickerState: sku-picker already has correct state (e.g. URL params),
      // just need the broadcast for frame/room mocks.
      sp._broadcastMockConfig();
    }

    // Reset hero mock zoom/offset to default (custom panel may have shifted it)
    this.#setHeroMockZoom(0.80);

    // Ensure full mocks are loaded then rewrite form properties with the
    // correct mock handle. Mocks are lazy-loaded, so _getSelectedMockHandle
    // returns null until they arrive — without this re-run on every preset
    // apply (not just init) the cart line's _mock_image_handle is omitted and
    // the thumbnail falls back to the variant's default (unframed) mock.
    const gallery = document.querySelector('media-gallery');
    if (gallery && typeof gallery.ensureFullMocksLoaded === 'function') {
      gallery.ensureFullMocksLoaded().then(() => {
        sp._updateFormProperties();
      });
    }

    this.#applyingConfig = false;
  }

  #getPresetName(id) {
    if (id === 'custom') return this.#customSourcePreset?.name || (this.#i18n.custom || 'Custom');
    const p = this.#presets.find(pr => pr.id === id);
    if (!p) return '';
    return p.name;
  }

  #getPresetDescription(id) {
    if (id === 'custom') return this.#customSourcePreset?.description || 'Your personalized framing selection.';
    const p = this.#presets.find(pr => pr.id === id);
    if (!p) return '';
    return p.description || '';
  }

  #buildAboutButton(id) {
    const p = (id === 'custom')
      ? this.#customSourcePreset
      : (this.#presets.find(pr => pr.id === id) || this.#allPresets.find(pr => pr.id === id));
    if (!p || (!p.curatorNotes && !p.galleryImages?.length)) return '';
    return `<button type="button" class="style-picker__about-btn" data-about-style><span class="style-picker__about-icon">i</span>${this.#i18n.about_this_style || 'About this style'}</button>`;
  }

  #openAboutModal() {
    const p = (this.#activeId === 'custom')
      ? this.#customSourcePreset
      : (this.#presets.find(pr => pr.id === this.#activeId) || this.#allPresets.find(pr => pr.id === this.#activeId));
    if (!p) return;

    // Remove existing modal if any
    this.querySelector('.sp-about-dialog')?.remove();

    // Build gallery — corner mock always first, then up to 2 gallery images
    const mockHTML = this.#buildCornerMockHTML(p);
    const imgs = (p.galleryImages || []).slice(0, 2);
    const count = 1 + imgs.length; // 1 (mock only), 2, or 3
    const imgsHTML = imgs.map(img =>
      `<img src="${img.url}" alt="${img.alt}" width="${img.width}" height="${img.height}" loading="lazy">`
    ).join('');
    const galleryHTML = `<div class="sp-about__gallery sp-about__gallery--${count}">
      <div class="sp-about__gallery-mock">${mockHTML}</div>
      ${imgsHTML}
    </div>`;

    // Build notes HTML (multi_line_text_field uses \n for line breaks)
    const notesHTML = p.curatorNotes
      ? `<div class="sp-about__notes">${p.curatorNotes.split('\n').filter(Boolean).map(line => `<p>${line}</p>`).join('')}</div>`
      : '';

    // Build option chips (framing, frame color, border) with thumbnails
    const chipsHTML = this.#buildAboutChips(p);
    const fitHTML = this.#buildFitRating(p);

    const dialog = document.createElement('dialog');
    dialog.className = 'sp-about-dialog';
    dialog.setAttribute('aria-labelledby', 'sp-about-title');
    dialog.innerHTML = `
      <form method="dialog" class="sp-about__backdrop"></form>
      <article class="sp-about__panel" role="document">
        <header class="sp-about__header">
          <h2 id="sp-about-title" class="sp-about__title">${p.name || (this.#i18n.about_this_style || 'About this Style')}</h2>
          <button type="button" class="sp-about__close" aria-label="${this.#i18n.close || 'Close'}">\u00d7</button>
        </header>
        <div class="sp-about__body">
          <div class="sp-about__gallery-wrap">
            ${galleryHTML}
            ${chipsHTML}
          </div>
          ${fitHTML}
          ${notesHTML}
        </div>
      </article>
    `;

    // Append to body, not `this`, so the dialog renders even when the
    // <style-picker> host is display:none (surgical PDP hides the legacy
    // picker but still calls openAboutDialog via [data-pdp-about-style]).
    document.body.appendChild(dialog);

    // Close handlers
    dialog.querySelector('.sp-about__close').addEventListener('click', () => dialog.close());
    dialog.querySelector('.sp-about__backdrop').addEventListener('click', () => dialog.close());
    dialog.addEventListener('cancel', (e) => { e.preventDefault(); dialog.close(); });
    dialog.addEventListener('close', () => dialog.remove());

    dialog.style.zIndex = '9999';
    dialog.showModal();

    document.dispatchEvent(new CustomEvent('style-picker:about-opened', {
      detail: { presetId: p.id, presetName: p.name }
    }));
  }

  #buildAboutChips(p) {
    const chips = [];
    const v = this.#variantMap[p.resolvedVariantId];
    if (!v) return '';

    // Combined frame + color chip (e.g. "Black Classic Frame")
    const framingOpt = v.framingOptions.find(f => f.handle === p.resolvedFraming);
    if (framingOpt && p.framing !== 'unframed') {
      const color = framingOpt.colors?.find(c => c.handle === p.frameColorHandle);
      const colorName = color?.short_name || color?.label || '';
      const frameName = framingOpt.label || framingOpt.name || framingOpt.short_name || '';
      const bgStyle = color?.thumbnail
        ? `background-image:url('${color.thumbnail}');background-size:cover;background-position:center;`
        : `background-color:${color?.color_value || p.hex || '#333'};`;
      chips.push(`<span class="sp-about__chip">
        <span class="sp-about__chip-thumb" style="${bgStyle}"></span>
        <span class="sp-about__chip-label">${colorName} ${frameName}</span>
      </span>`);
    } else if (framingOpt) {
      // Unframed — just show framing name
      chips.push(`<span class="sp-about__chip">
        <span class="sp-about__chip-thumb" style="background:#f0f0f0;"></span>
        <span class="sp-about__chip-label">${framingOpt.label || framingOpt.name || (this.#i18n.unframed || 'Unframed')}</span>
      </span>`);
    }

    // Border option chip
    const variantConfig = this.#skuPicker.variantConfigurators.variants[p.resolvedVariantId];
    const borderOptions = variantConfig?.options?.border || [];
    const borderOpt = borderOptions.find(b => b.handle === p.border);
    if (borderOpt) {
      const bgStyle = borderOpt.thumbnail
        ? `background-image:url('${borderOpt.thumbnail}');background-size:cover;background-position:center;`
        : 'background:#f0f0f0;';
      chips.push(`<span class="sp-about__chip">
        <span class="sp-about__chip-thumb" style="${bgStyle}"></span>
        <span class="sp-about__chip-label">${borderOpt.label}</span>
      </span>`);
    }

    if (!chips.length) return '';
    return `<div class="sp-about__chips">${chips.join('')}</div>`;
  }

  #buildFitRating(p) {
    const score = this.#scorePreset(p);
    const hasLlm = this.#suggestedColors.length > 0 || this.#suggestedBorder !== '';
    if (!hasLlm || score < 0) return '';

    const segments = [
      { label: this.#i18n.rating_fair || 'Fair',           color: '#e4a070' },
      { label: this.#i18n.rating_good || 'Good',           color: '#d4c05a' },
      { label: this.#i18n.rating_great || 'Great',         color: '#7bb87b' },
      { label: this.#i18n.rating_excellent || 'Excellent', color: '#4a9a4a' },
    ];
    const idx = Math.min(score, 3);
    const midpct = idx * 25 + 12.5;

    return `<div class="sp-about__fit">
      <div class="sp-about__fit-header">
        <span class="sp-about__fit-heading">${this.#i18n.artwork_fit || 'Artwork Fit'}</span>
        <span class="sp-about__fit-label">${segments[idx].label}</span>
      </div>
      <div class="sp-about__fit-bar">
        <span class="sp-about__fit-indicator" style="left:${midpct}%"></span>
        ${segments.map(s => `<span class="sp-about__fit-seg" style="background:${s.color}"></span>`).join('')}
      </div>
    </div>`;
  }

  /** Returns CSS corner-crop mock HTML for the About dialog (no canvas).
   *  Mirrors the surgical picker's CSS corner-crop, fed by media-gallery's
   *  getProductMockForPreset (frame-overlay image + placement). */
  #buildCornerMockHTML(p) {
    // Normalize CDN images to a thumbnail width + quality (frame mocks otherwise
    // load full-res — e.g. a 439KB canvas texture). Same-file artwork requests
    // reuse the hero's exact in-DOM URL — a browser-cache hit instead of a
    // refetch at a unique size.
    const heroImg = document.querySelector('img.mock-image__artwork');
    const heroSrc = (heroImg && (heroImg.currentSrc || heroImg.src)) || '';
    const pathOf = (u) => u.split('?')[0].replace(/^(https?:)?\/\//, '');
    const sized = (u, w = 700, q = 70) => {
      if (!u || typeof u !== 'string' || !u.includes('/')) return u || '';
      if (heroSrc && pathOf(u) === pathOf(heroSrc)) return heroSrc;
      const [base, qs = ''] = u.split('?');
      const params = new URLSearchParams(qs);
      params.delete('width'); params.delete('height');
      params.set('width', String(w)); params.set('quality', String(q));
      return `${base}?${params.toString()}`;
    };
    // Prefer the SSR'd artwork URL — reliable at first render (the live hero
    // <img> may not have a src yet).
    const artUrl =
      this.dataset.artworkUrl ||
      document.querySelector('[data-slide-role="frame-mock"] .mock-image__placement img')?.currentSrc ||
      document.querySelector('[data-slide-role="frame-mock"] .mock-image__placement img')?.src ||
      document.querySelector('.mock-image__placement img')?.src || '';
    const framing = p.resolvedFraming || p.framing;
    const isUnframed = framing === 'unframed';
    const gallery = document.querySelector('media-gallery');
    // Unframed/canvas have real mocks too — resolve like any framing.
    const mock = gallery?.getProductMockForPreset
      ? gallery.getProductMockForPreset({
          resolvedVariantId: p.resolvedVariantId,
          frameColorHandle: isUnframed ? null : p.frameColorHandle,
          border: p.border || 'no-border',
          resolvedFraming: framing,
        })
      : null;
    if (!mock || !mock.frameImage) {
      return `<div class="sp-about-cc sp-about-cc--bare"><img src="${sized(artUrl, 1000, 75)}" alt="" loading="lazy"></div>`;
    }
    // Anchor the artwork bottom-left corner (same formula as the surgical trio
    // cards) so every aspect — portrait/square/landscape/canvas — crops
    // consistently; the white .sp-about-cc background blends non-bleed padding.
    // (Anchoring the image's own corner doesn't work: unframed/padded mocks
    // have large empty margins inside the file, so only the artwork placement
    // is reliable geometry.)
    let aspect = parseFloat(mock.aspectRatio);
    if (!aspect || !isFinite(aspect)) {
      aspect = parseFloat(this.dataset.artworkAspect || '');
    }
    if (!aspect || !isFinite(aspect)) {
      const ai = document.querySelector('media-gallery .mock-image__placement img');
      aspect = (ai && ai.naturalWidth && ai.naturalHeight) ? ai.naturalWidth / ai.naturalHeight : 1;
    }
    const ax = (parseFloat(mock.placementLeft) || 0) / 100;
    const ay = ((parseFloat(mock.placementTop) || 0) + (parseFloat(mock.placementWidth) || 100) / aspect) / 100;
    // K=2.8 (was 4.8) zooms out so the full moulding band (~5-8% of the image
    // width) fits between the anchor and the floating chip row that overlays
    // the bottom ~22% of the box; TX/TY place the artwork corner so the frame's
    // outer corner lands inside the visible window. Lower zoom also reduces
    // the upscale of the 1400px source assets.
    const K = 2.8, TX = 0.42, TY = 0.55;
    const innerStyle = `left:${((TX - ax * K) * 100).toFixed(2)}%;top:${((TY - ay * K) * 100).toFixed(2)}%;bottom:auto;width:${K * 100}%;height:auto;aspect-ratio:1;`;
    // No loading="lazy" — corner-crop images are positioned off-screen, so
    // Safari's lazy-loader never fires (image stays 0x0 → artwork invisible).
    // The About corner crop renders the frame at ~1200 CSS px (~3100 device px
    // on mobile) — at the old 700px source it was visibly pixelated. Frame
    // bleed images are flat-colour webp (~7 KB at 1600); artwork caps at its
    // native width, so 1000 requests the best available.
    return `<div class="sp-about-cc">
      <div class="sp-about-cc__inner" style="${innerStyle}">
        <img class="sp-about-cc__frame" src="${sized(mock.frameImage, 1600, 75)}" alt="">
        <div class="sp-about-cc__art" style="top:${mock.placementTop}%;left:${mock.placementLeft}%;width:${mock.placementWidth}%;transform:${mock.transform || 'none'};">
          <img src="${sized(artUrl, 1000, 75)}" alt="">
        </div>
      </div>
    </div>`;
  }

  #setActiveCard(id) {
    this.#activeId = id;
    this.querySelectorAll('.style-picker__card').forEach(card => {
      card.classList.toggle('style-picker__card--active', card.dataset.styleId === id);
    });
    // Show/hide Custom card
    const customCard = this.querySelector('[data-style-id="custom"]');
    if (customCard) {
      const hideCustom = id !== 'custom' && !this.#hasUserCustomSelection;
      customCard.classList.toggle('style-picker__card--hidden', hideCustom);
    }
    const valueEl = this.querySelector('.style-picker__header-value');
    if (valueEl) valueEl.textContent = this.#getPresetName(id);
    const aboutBtnContainer = this.querySelector('.style-picker__header-title');
    const oldBtn = aboutBtnContainer?.querySelector('.style-picker__about-btn');
    if (oldBtn) oldBtn.remove();
    if (aboutBtnContainer) aboutBtnContainer.insertAdjacentHTML('beforeend', this.#buildAboutButton(id));

    // Update description with crossfade
    const descEl = this.querySelector('.style-picker__header-description');
    if (descEl) {
      const newDesc = this.#getPresetDescription(id);
      if (descEl.textContent !== newDesc) {
        descEl.classList.add('is-fading');
        setTimeout(() => {
          descEl.textContent = newDesc;
          descEl.classList.remove('is-fading');
        }, 150);
      }
    }
  }

  /* ── Custom card ── */

  /**
   * Show the custom/overflow card in the row. If sourcePreset is provided it's used
   * directly; otherwise we try to match current sku-picker state against #allPresets.
   */
  #showCustomCard(sourcePreset = null, { isCustomBuilt = false } = {}) {
    const card = this.querySelector('[data-style-id="custom"]');
    if (!card) return;

    // Use source preset's resolved data when available (sku-picker state may
    // not have propagated yet if #applyPresetConfig was just called).
    const vid = sourcePreset?.resolvedVariantId || this.#skuPicker.currentVariantId;
    const v = this.#variantMap[vid];
    const artProduct = v?.artProduct;
    const productType = artProduct === 'canvas-print' ? 'canvas' : 'art_print';

    let hex, isUnframed, mouldingTile, mouldingWidth, borderOption;
    if (sourcePreset) {
      hex = sourcePreset.hex || '#333';
      isUnframed = sourcePreset.framing === 'unframed';
      mouldingTile = isUnframed ? '' : (sourcePreset.mouldingTile || '');
      mouldingWidth = isUnframed ? 0 : (sourcePreset.mouldingWidth || null);
      borderOption = sourcePreset.border;
    } else {
      const sel = this.#skuPicker.state.confirmed;
      const framingOpt = v?.framingOptions.find(f => f.handle === sel.framing_option);
      const color = framingOpt?.colors?.find(c => c.handle === sel.frame_color);
      hex = color?.color_value || COLOR_HEX[color?.short_name?.toLowerCase()] || '#333';
      isUnframed = sel.framing_option === 'unframed';
      mouldingTile = isUnframed ? '' : (color?.moulding_tile || '');
      mouldingWidth = isUnframed ? 0 : (color?.moulding_width || null);
      borderOption = sel.border_option;
    }

    const mock = card.querySelector('gallery-wall-frame-mock');
    if (mock) {
      mock.updateConfig({
        frameColor: hex,
        mouldingTile,
        mouldingWidth,
        borderWidth: this.#borderWidthCm(borderOption),
        borderOption,
        hasFrame: !isUnframed,
        productType,
      });
    }

    // Resolve the source preset — try to find a matching allPreset for proper naming.
    // Skip resolution when the user explicitly built via the Custom Style tab;
    // their config should always use the non-style cart format (separate Size/Frame/Border).
    if (!sourcePreset && !isCustomBuilt) {
      const sel = this.#skuPicker.state.confirmed;
      sourcePreset = this.#allPresets.find(p =>
        p.resolvedVariantId === vid &&
        p.resolvedFraming === sel.framing_option &&
        p.frameColorHandle === sel.frame_color &&
        p.border === sel.border_option
      ) || null;
    }
    this.#customSourcePreset = isCustomBuilt ? null : sourcePreset;

    // Tell sku-picker the active style name: use source preset name if backed
    // by a named preset, null for custom-built configs (triggers classic format).
    this.#skuPicker.activeStyleName = isCustomBuilt ? null : (sourcePreset?.name || null);

    // Update the card's name label
    const nameEl = card.querySelector('.style-picker__name');
    if (nameEl) nameEl.textContent = isCustomBuilt
      ? (this.#i18n.custom || 'Custom')
      : (sourcePreset?.name || (this.#i18n.custom || 'Custom'));

    this.#setActiveCard('custom');
  }

  /* ── Size-dependent availability handling ── */

  #handleSizeChange() {
    // Recalculate which 4 presets belong in the row for the new size
    this.#pickRowPresets();

    // Availability-first (Phase 1): if the user's current confirmed selection
    // (size + framing + colour + border) is still purchasable at the new size,
    // keep it exactly as-is. This preserves BYO custom configurations that match
    // no curated preset — the previous preset-match check below reset them even
    // when they remained available. Only fall through to a default when the
    // combo is genuinely unavailable at the new size.
    const confirmedSel = this.#skuPicker?.state?.confirmed;
    if (confirmedSel?.framing_option
        && this.#skuPicker._getSelectionDetails?.(confirmedSel)?.available) {
      this.#updateRowCards();
      return;
    }

    // Check if the active preset (whether row or custom-source) is still available
    const activeRowPreset = this.#presets.find(p => p.id === this.#activeId);
    const activeAllPreset = this.#activeId === 'custom' && this.#customSourcePreset;
    const activePreset = activeRowPreset || activeAllPreset;

    if (activePreset && this.#isPresetAvailable(activePreset)) {
      // Even though the active preset is still available, check if the user's
      // intended preset should be restored.  #preferredStyleId is only set on
      // explicit user clicks, never by auto-fallback, so it reliably represents
      // what the user actually wanted.  When null (no explicit click yet), fall
      // back to presets[0] — the highest-scored available preset for this size.
      const intendedId = this.#preferredStyleId || this.#presets[0]?.id;
      if (intendedId && intendedId !== this.#activeId) {
        // Check row presets
        const intendedRow = this.#presets.find(p => p.id === intendedId);
        if (intendedRow && this.#isPresetAvailable(intendedRow)) {
          this.#hasUserCustomSelection = false;
          this.#handleCardClick(intendedRow);
          this.#updateRowCards();
          return;
        }
        // Check all presets (may show as custom card)
        const intendedAll = this.#allPresets.find(p => p.id === intendedId);
        if (intendedAll && this.#isPresetAvailable(intendedAll)) {
          this.#hasUserCustomSelection = true;
          this.#applyPresetConfig(intendedAll);
          this.#showCustomCard(intendedAll);
          this.#updateRowCards();
          return;
        }
      }
      // Intended preset isn't available or matches active — keep current
      this.#updateRowCards();
      return;
    }

    // Truly custom config (no source preset) — check if still valid for the new size
    if (this.#activeId === 'custom' && this.#hasUserCustomSelection && this.#customTabState) {
      if (this.#isCustomTabAvailable()) {
        this.#updateRowCards();
        return;
      }
    }

    // External custom selection (e.g. surgical frame picker) with no custom tab state —
    // keep the current framing if it still matches an available preset for the new size.
    if (this.#activeId === 'custom' && this.#hasUserCustomSelection && !this.#customTabState) {
      const sp = this.#skuPicker;
      const sel = sp.state.confirmed;
      const stillValid = this.#allPresets.some(p =>
        p.resolvedVariantId === sp.currentVariantId &&
        p.resolvedFraming === sel.framing_option &&
        p.frameColorHandle === sel.frame_color &&
        p.border === sel.border_option &&
        this.#isPresetAvailable(p)
      );
      if (stillValid) {
        this.#updateRowCards();
        return;
      }
    }

    // Try to revert to user's preferred style if it's now available
    if (this.#preferredStyleId) {
      // Check row presets first
      const preferredRow = this.#presets.find(p => p.id === this.#preferredStyleId);
      if (preferredRow && this.#isPresetAvailable(preferredRow)) {
        this.#hasUserCustomSelection = false;
        this.#handleCardClick(preferredRow);
        this.#updateRowCards();
        return;
      }
      // Check all presets (may show as custom card)
      const preferredAll = this.#allPresets.find(p => p.id === this.#preferredStyleId);
      if (preferredAll && this.#isPresetAvailable(preferredAll)) {
        this.#hasUserCustomSelection = true;
        this.#applyPresetConfig(preferredAll);
        this.#showCustomCard(preferredAll);
        this.#updateRowCards();
        return;
      }
    }

    // Fall back to first available row preset
    this.#hasUserCustomSelection = false;
    const firstAvailable = this.#presets.find(p => this.#isPresetAvailable(p));
    if (firstAvailable) {
      this.#handleCardClick(firstAvailable);
    } else {
      // No row preset available — try first available from all presets
      const firstAllAvailable = this.#allPresets.find(p => this.#isPresetAvailable(p));
      if (firstAllAvailable) {
        this.#hasUserCustomSelection = true;
        this.#applyPresetConfig(firstAllAvailable);
        this.#showCustomCard(firstAllAvailable);
      }
    }

    this.#updateRowCards();
  }

  /* ── External sync (modal changes reflected in style picker) ── */

  #listenForExternalChanges() {
    // Store handler refs so disconnectedCallback can remove them.
    // Use rAF debounce so rapid slider drags collapse to one update per frame.
    let sizeChangeRaf = 0;
    const sizeChanged = () => {
      cancelAnimationFrame(sizeChangeRaf);
      sizeChangeRaf = requestAnimationFrame(() => {
        this.#handleSizeChange();

        // If custom tab is open, resync state (handleSizeChange may have applied a fallback) and re-render
        if (this.#activeTab === 'custom' && this.#sheetDialog?.open && this.#customPanelBuilt) {
          this.#reinitCustomTabState();
        }
      });
    };

    const selectionChanged = (e) => {
      if (this.#applyingConfig) return; // Ignore self-triggered events
      const sel = e.detail?.selection;
      if (!sel) return;
      const vid = this.#skuPicker.currentVariantId;
      const matcher = p =>
        p.resolvedVariantId === vid &&
        p.resolvedFraming === sel.framing_option &&
        p.frameColorHandle === sel.frame_color &&
        p.border === sel.border_option;
      // Search the row first, then all presets — a configured selection may
      // match a preset that lives in the "View All" overflow rather than the
      // row (e.g. framed canvas styles). Resolving against the row alone would
      // mislabel it as custom and leave activeStyleName stale, so the cart's
      // "Style" line shows the wrong (initial) preset name.
      const match = this.#presets.find(matcher) || this.#allPresets.find(matcher);
      if (match) {
        this.#skuPicker.activeStyleName = match.name || null;
        // Update preferred so size-change fallback doesn't revert to stale init state
        if (e.detail?.userInitiated) this.#preferredStyleId = match.id;
        if (this.#presets.includes(match)) {
          this.#hasUserCustomSelection = false;
          this.#setActiveCard(match.id);
        } else {
          this.#hasUserCustomSelection = true;
          this.#showCustomCard(match);
        }
      } else {
        this.#hasUserCustomSelection = true;
        this.#skuPicker.activeStyleName = null;
        this.#showCustomCard();
      }

      // Sync row card active states
      this.#updateRowCards();
    };

    // Re-detect active preset when variant changes from external sources (URL navigation, etc.)
    const variantChanged = () => {
      if (this.#applyingConfig) return; // Ignore self-triggered events
      this.#hasUserCustomSelection = false;
      this.#detectActivePreset();
      this.#hideSkuPickerRows();
    };

    // Listen for size changes from the size slider.
    // Defer to a separate macrotask so the slider's own config-changed renders
    // complete before the style-picker potentially dispatches another config-changed.
    const unitChanged = () => {
      // No-op: pills removed; about button doesn't depend on unit
    };

    document.addEventListener('size-impact:changed', sizeChanged);
    document.addEventListener('sku-picker:selection-changed', selectionChanged);
    document.addEventListener('sku-picker:variant-changed', variantChanged);
    document.addEventListener('sku-picker:unit-changed', unitChanged);

    // Listen for external configuration requests (e.g. from the Design Advice app block)
    const applyConfiguration = (e) => {
      const sp = this.#skuPicker;
      const { sizeHandle, styleId, framingHandle, frameColorHandle, borderOption } = e.detail || {};

      // Sync size first (if different from current)
      if (sizeHandle && sizeHandle !== sp.state.confirmed.size_option) {
        sp.state.confirmed.size_option = sizeHandle;
        // Sync size-impact-slider UI
        const sizeSlider = document.querySelector('size-impact-slider');
        if (sizeSlider) {
          const sizes = sp.data.options.size || [];
          const idx = sizes.findIndex(s => s.handle === sizeHandle);
          if (idx >= 0) {
            const range = sizeSlider.querySelector('.size-bucket-slider__range');
            if (range && parseInt(range.value, 10) !== idx) {
              range.value = idx;
              range.dispatchEvent(new Event('input', { bubbles: true }));
            }
          }
        }
      }

      // Try to match a preset by styleId
      if (styleId) {
        const preset = this.#allPresets.find(p => p.id === styleId);
        if (preset) {
          this.#handleCardClick(preset);
          return;
        }
      }

      // Fallback: apply handles directly
      if (framingHandle) sp.state.confirmed.framing_option = framingHandle;
      if (frameColorHandle) sp.state.confirmed.frame_color = frameColorHandle;
      if (borderOption) sp.state.confirmed.border_option = borderOption;

      // Enforce constraint: mount requires frame
      const isUnframed = sp.state.confirmed.framing_option === 'unframed'
        || sp.state.confirmed.framing_option?.endsWith('-unframed');
      if (isUnframed && sp.state.confirmed.border_option === 'mount') {
        sp.state.confirmed.border_option = 'no-border';
      }

      // Resolve the matching preset BEFORE _updateConfirmedSelection so the
      // form properties (the cart "Style" line) pick up the correct preset
      // name. Search all presets, not just the row, so framed-canvas styles
      // in the "View All" overflow still resolve instead of going stale.
      const vid = sp.currentVariantId;
      const matcher = p =>
        p.resolvedVariantId === vid &&
        p.resolvedFraming === sp.state.confirmed.framing_option &&
        p.frameColorHandle === sp.state.confirmed.frame_color &&
        p.border === sp.state.confirmed.border_option;
      const match = this.#presets.find(matcher) || this.#allPresets.find(matcher);
      sp.activeStyleName = match ? (match.name || null) : null;

      sp._updateConfirmedSelection(true);

      // Update style-picker UI — show matching preset or custom card
      if (match && this.#presets.includes(match)) {
        this.#setActiveCard(match.id);
      } else if (match) {
        this.#showCustomCard(match);
      } else {
        this.#showCustomCard();
      }
      this.#updateRowCards();
    };

    document.addEventListener('pdp:apply-configuration', applyConfiguration);

    // Cross-PDP handoff: the advisor drawer stages a config under
    // `pdp:pending-config:<handle>` in sessionStorage before redirecting to
    // a different PDP (e.g. a frame recommendation for a product the user
    // wasn't already viewing). Drain it now that the listener is live.
    try {
      const m = window.location.pathname.match(/\/products\/([^/?#]+)/);
      const handle = m ? decodeURIComponent(m[1]) : null;
      if (handle) {
        const key = `pdp:pending-config:${handle}`;
        const raw = sessionStorage.getItem(key);
        if (raw) {
          sessionStorage.removeItem(key);
          const detail = JSON.parse(raw);
          applyConfiguration({ detail });
        }
      }
    } catch { /* ignore */ }

    this.#externalListenerHandlers = { sizeChanged, selectionChanged, variantChanged, unitChanged, applyConfiguration };

    // Re-hide rows after sku-picker re-renders (e.g., size change triggers _renderOptionRows)
    this.#skuPickerObserver = new MutationObserver(() => {
      this.#hideSkuPickerRows();
    });
    const container = this.#skuPicker.container;
    if (container) {
      this.#skuPickerObserver.observe(container, { childList: true });
    }
  }

  /* ── "View All" sheet / drawer ── */

  #groupPresets() {
    const MEDIUM_ORDER = ['Art Print', 'Canvas'];
    const VIBE_ORDER = ['Modern', 'Classical', 'Neutral'];
    const groups = {};

    for (const p of this.#allPresets) {
      const medium = p.productType === 'canvas-print' ? 'Canvas' : 'Art Print';
      const vibe = p.vibe.charAt(0).toUpperCase() + p.vibe.slice(1);
      const key = `${medium}|${vibe}`;
      (groups[key] ??= []).push(p);
    }

    // Return ordered entries
    const ordered = [];
    for (const med of MEDIUM_ORDER) {
      for (const vib of VIBE_ORDER) {
        const key = `${med}|${vib}`;
        if (groups[key]?.length) {
          ordered.push({ medium: med, vibe: vib, presets: groups[key] });
        }
      }
    }
    return ordered;
  }

  #buildSheetCardHTML(preset, sharedAttrs) {
    const active = preset.id === this.#activeId ? ' style-picker-sheet__card--active' : '';
    const disabled = !this.#isPresetAvailable(preset) ? ' style-picker-sheet__card--disabled' : '';
    const productType = preset.productType === 'canvas-print' ? 'canvas' : 'art_print';
    const isUnframed = preset.framing === 'unframed';

    const score = this.#scorePreset(preset);
    const recommendedBadge = score >= 2 && !disabled ? `<span class="style-picker-sheet__recommended">${this.#i18n.recommended || 'Recommended'}</span>` : '';
    const priceText = this.#buildCardPrice(preset);
    const priceHTML = priceText ? `<span class="style-picker-sheet__card-price">${priceText}</span>` : '';

    return `<button class="style-picker-sheet__card${active}${disabled}" data-style-id="${preset.id}" type="button">
      <div class="style-picker-sheet__card-preview">
        <gallery-wall-frame-mock class="style-picker__frame-preview"
          data-corner-crop="true"
          data-artwork-url="${sharedAttrs.artworkUrl}"
          data-artwork-aspect="${sharedAttrs.artworkAspect}"
          data-wall-texture="${sharedAttrs.wallTexture}"
          data-room-lighting-portrait="${sharedAttrs.roomLightingPortrait}"
          data-room-lighting-landscape="${sharedAttrs.roomLightingLandscape}"
          data-room-lighting-square="${sharedAttrs.roomLightingSquare}"
          data-moulding-tile="${isUnframed ? '' : (preset.mouldingTile || sharedAttrs.mouldingTile)}"
          data-moulding-width="${isUnframed ? '0' : (preset.mouldingWidth || sharedAttrs.mouldingWidth)}"
          data-frame-color="${preset.hex || ''}"
          data-border-width="${this.#borderWidthCm(preset.border)}"
          data-border-option="${preset.border}"
          data-has-frame="${!isUnframed}"
          data-product-type="${productType}"
        ><canvas></canvas></gallery-wall-frame-mock>
        <span class="style-picker-sheet__card-badge">${this.#i18n.select_different_size || 'Select a different size'}</span>
        ${recommendedBadge}
      </div>
      <span class="style-picker-sheet__card-name">${preset.name}</span>
      <span class="style-picker-sheet__card-desc">${preset.description || ''}</span>
      ${priceHTML}
    </button>`;
  }

  #getSharedMockAttrs() {
    // Prefer the <style-picker>'s own data attributes — the same source the row
    // cards use in #render(). They're SSR-set for standard art PDPs and, on a
    // personalise PDP, personalise-canvas.js keeps data-artwork-url in sync with
    // the live SVG preview (which has no page hero frame-mock to read from).
    // Fall back to the page hero mock for any attribute the style-picker lacks.
    const pageMock = document.querySelector('gallery-wall-frame-mock:not([data-corner-crop])');
    return {
      artworkUrl: this.dataset.artworkUrl || pageMock?.dataset.artworkUrl || '',
      artworkAspect: this.dataset.artworkAspect || pageMock?.dataset.artworkAspect || '1',
      wallTexture: this.dataset.wallTexture || pageMock?.dataset.wallTexture || '',
      roomLightingPortrait: this.dataset.roomLightingPortrait || pageMock?.dataset.roomLightingPortrait || '',
      roomLightingLandscape: this.dataset.roomLightingLandscape || pageMock?.dataset.roomLightingLandscape || '',
      roomLightingSquare: this.dataset.roomLightingSquare || pageMock?.dataset.roomLightingSquare || '',
      mouldingTile: pageMock?.dataset.mouldingTile || '',
      mouldingWidth: pageMock?.dataset.mouldingWidth || '15',
    };
  }

  #buildPresetsBodyHTML(sharedAttrs) {
    const groups = this.#groupPresets();
    let bodyHTML = '';
    let currentMedium = '';
    const unavailablePresets = [];

    for (const group of groups) {
      const available = group.presets.filter(p => this.#isPresetAvailable(p));
      const unavailable = group.presets.filter(p => !this.#isPresetAvailable(p));
      unavailablePresets.push(...unavailable);

      if (available.length === 0) continue;

      if (group.medium !== currentMedium) {
        currentMedium = group.medium;
        bodyHTML += `<h3 class="style-picker-sheet__group-title">${group.medium}</h3>`;
      }
      bodyHTML += `<h4 class="style-picker-sheet__vibe-title">${group.vibe}</h4>`;
      bodyHTML += `<div class="style-picker-sheet__grid">`;
      for (const preset of available) {
        bodyHTML += this.#buildSheetCardHTML(preset, sharedAttrs);
      }
      bodyHTML += `</div>`;
    }

    if (unavailablePresets.length > 0) {
      bodyHTML += `<h3 class="style-picker-sheet__group-title style-picker-sheet__group-title--unavailable">${this.#i18n.unavailable_in_size || 'Unavailable in this size'}</h3>`;
      bodyHTML += `<div class="style-picker-sheet__grid">`;
      for (const preset of unavailablePresets) {
        bodyHTML += this.#buildSheetCardHTML(preset, sharedAttrs);
      }
      bodyHTML += `</div>`;
    }

    return bodyHTML;
  }

  #createSheet() {
    const sharedAttrs = this.#getSharedMockAttrs();
    const bodyHTML = this.#buildPresetsBodyHTML(sharedAttrs);

    const dialog = document.createElement('dialog');
    dialog.className = 'style-picker-sheet';
    dialog.innerHTML = `
      <div class="style-picker-sheet__header">
        <div class="style-picker-sheet__tabs">
          <button class="style-picker-sheet__tab style-picker-sheet__tab--active" data-tab="presets">${this.#i18n.all_styles || 'All Styles'}</button>
          <button class="style-picker-sheet__tab" data-tab="custom">${this.#i18n.custom_style || 'Custom Style'}</button>
        </div>
        <button class="style-picker-sheet__close" type="button" aria-label="${this.#i18n.close || 'Close'}">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>

      <div class="style-picker-sheet__body style-picker-sheet__panel--active" data-panel="presets">${bodyHTML}</div>

      <div class="style-picker-sheet__body" data-panel="custom">
        <!-- lazily built by #buildCustomPanel() -->
      </div>

      <div class="style-picker-sheet__footer style-picker-sheet__footer--hidden">
        <div class="style-picker-sheet__price">
          <span class="style-picker-sheet__price-label">${this.#i18n.total || 'Total'}</span>
          <span class="style-picker-sheet__price-value"></span>
        </div>
        <button type="button" class="style-picker-sheet__apply">${this.#i18n.apply || 'Apply'}</button>
      </div>
    `;

    // Tab click listeners
    dialog.querySelectorAll('.style-picker-sheet__tab').forEach(tab => {
      tab.addEventListener('click', () => this.#switchTab(tab.dataset.tab));
    });

    // Close button
    dialog.querySelector('.style-picker-sheet__close').addEventListener('click', () => {
      this.#closeSheet();
    });

    // Apply button — commits custom config and closes sheet
    dialog.querySelector('.style-picker-sheet__apply').addEventListener('click', () => {
      this.#applyCustomConfig();
      this.#closeSheet();
    });

    // Backdrop click to close
    dialog.addEventListener('click', (e) => {
      if (e.target === dialog) this.#closeSheet();
    });

    // Prevent native dialog cancel (Escape) to run animated close instead
    dialog.addEventListener('cancel', (e) => {
      e.preventDefault();
      this.#closeSheet();
    });

    this.appendChild(dialog);
    this.#sheetDialog = dialog;
  }

  #openSheet(initialTab = 'presets') {
    if (!this.#sheetDialog) this.#createSheet();

    // Rebuild presets panel with current availability sorting
    const presetsPanel = this.#sheetDialog.querySelector('[data-panel="presets"]');
    if (presetsPanel) {
      const sharedAttrs = this.#getSharedMockAttrs();
      presetsPanel.innerHTML = this.#buildPresetsBodyHTML(sharedAttrs);

      // Rebind card click listeners on the fresh presets panel
      presetsPanel.querySelectorAll('.style-picker-sheet__card').forEach(card => {
        card.addEventListener('click', () => {
          const preset = this.#allPresets.find(p => p.id === card.dataset.styleId);
          if (!preset || !this.#isPresetAvailable(preset)) return;

          this.#preferredStyleId = preset.id;

          const inRow = this.#presets.some(p => p.id === preset.id);
          this.#applyPresetConfig(preset);
          if (inRow) {
            this.#hasUserCustomSelection = false;
            this.#setActiveCard(preset.id);
            this.#updateRowCards();
          } else {
            this.#hasUserCustomSelection = true;
            this.#showCustomCard(preset);
            this.#updateRowCards();
          }

          document.dispatchEvent(new CustomEvent('style-picker:preset-selected', {
            detail: {
              presetId: preset.id,
              presetName: preset.name,
              framing: preset.resolvedFraming,
              frameColor: preset.frameColorHandle,
              border: preset.border,
              source: 'sheet',
            }
          }));

          this.#switchFromLifestyleSlide();
          this.#closeSheet();
        });
      });
    }

    // Update active state in sheet
    this.#sheetDialog.querySelectorAll('.style-picker-sheet__card').forEach(card => {
      card.classList.toggle('style-picker-sheet__card--active', card.dataset.styleId === this.#activeId);
    });

    // Lock scroll
    document.body.style.width = '100%';
    const scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;

    this.#sheetDialog.showModal();

    document.dispatchEvent(new CustomEvent('style-picker:sheet-opened', {
      detail: { initialTab }
    }));

    // Switch to requested tab
    this.#switchTab(initialTab);

    // Flush dirty corner-crop mocks only in the active panel (others stay deferred)
    const activePanel = this.#sheetDialog.querySelector('[data-panel].style-picker-sheet__panel--active');
    if (activePanel) {
      activePanel.querySelectorAll('gallery-wall-frame-mock').forEach(el => {
        if (el.flushIfDirty) el.flushIfDirty();
      });
    }
  }

  #closeSheet() {
    const dialog = this.#sheetDialog;
    if (!dialog?.open) return;

    dialog.classList.add('style-picker-sheet--closing');

    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      dialog.removeEventListener('animationend', finish);
      dialog.classList.remove('style-picker-sheet--closing');

      // Reset mock zoom and scroll state
      this.#setHeroMockZoom(0.80);
      this.#savedScrollBeforeCustom = null;

      // Restore scroll
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
      window.scrollTo({ top: parseInt(scrollY) * -1, behavior: 'instant' });

      dialog.close();
    };

    dialog.addEventListener('animationend', finish);
    // Fallback in case animationend never fires (CSS not loaded, etc.)
    setTimeout(finish, 350);
  }

  /* ── Custom Style Tab ── */

  #switchTab(tabName) {
    if (!this.#sheetDialog) return;
    const prevTab = this.#activeTab;
    this.#activeTab = tabName;
    this.#sheetDialog.dataset.activeTab = tabName;

    if (prevTab !== tabName) {
      document.dispatchEvent(new CustomEvent('style-picker:tab-switched', {
        detail: { fromTab: prevTab, toTab: tabName }
      }));
    }

    // Toggle active class on tab buttons
    this.#sheetDialog.querySelectorAll('.style-picker-sheet__tab').forEach(tab => {
      tab.classList.toggle('style-picker-sheet__tab--active', tab.dataset.tab === tabName);
    });

    // Toggle panel visibility
    this.#sheetDialog.querySelectorAll('[data-panel]').forEach(panel => {
      panel.classList.toggle('style-picker-sheet__panel--active', panel.dataset.panel === tabName);
    });

    // Show/hide price footer (visible only on custom tab)
    const footer = this.#sheetDialog.querySelector('.style-picker-sheet__footer');
    if (footer) {
      footer.classList.toggle('style-picker-sheet__footer--hidden', tabName !== 'custom');
    }

    if (tabName === 'custom') {
      if (!this.#customPanelBuilt) {
        this.#buildCustomPanel();
      } else {
        this.#reinitCustomTabState();
      }

      // Lock the custom panel height so toggling steps doesn't shift the sheet
      if (window.innerWidth < 750) {
        this.#lockCustomPanelHeight();
      }

      // Mobile: scroll gallery flush below sticky header, zoom out mock, switch to frame slide
      if (window.innerWidth < 750) {
        this.#scrollGalleryIntoView();
        this.#setHeroMockZoom(0.52, -0.15);
      }
      // Defer slide switch to next frame so gallery/slideshow is fully initialized
      requestAnimationFrame(() => this.#switchToFrameSlide());
    }

    // Flush dirty corner-crop frame mocks when their panel becomes visible
    if (tabName === 'presets') {
      // Mobile: restore original scroll position and reset mock zoom when leaving custom tab
      if (window.innerWidth < 750) {
        if (this.#savedScrollBeforeCustom != null) {
          document.body.style.top = `-${this.#savedScrollBeforeCustom}px`;
          this.#savedScrollBeforeCustom = null;
        }
        this.#setHeroMockZoom(0.80);
      }

      const panel = this.#sheetDialog.querySelector('[data-panel="presets"]');
      if (panel) {
        panel.querySelectorAll('gallery-wall-frame-mock').forEach(el => {
          if (el.flushIfDirty) el.flushIfDirty();
        });
      }
    }
  }

  #scrollGalleryIntoView() {
    const gallery = document.querySelector('media-gallery') || document.querySelector('gallery-wall-media-gallery');
    if (!gallery) return;

    const currentFakeScroll = Math.abs(parseInt(document.body.style.top) || 0);
    this.#savedScrollBeforeCustom = currentFakeScroll;

    const headerHeight = parseInt(getComputedStyle(document.body).getPropertyValue('--header-height')) || 0;
    // Use offsetTop to get the gallery's natural document position
    // (getBoundingClientRect is unreliable for sticky elements)
    let galleryAbsTop = gallery.offsetTop;
    let el = gallery.offsetParent;
    while (el && el !== document.body) {
      galleryAbsTop += el.offsetTop;
      el = el.offsetParent;
    }
    const targetScroll = Math.max(0, galleryAbsTop - headerHeight);
    document.body.style.top = `-${targetScroll}px`;
  }

  #setHeroMockZoom(fraction, offsetY = 0) {
    const gallery = document.querySelector('media-gallery') || document.querySelector('gallery-wall-media-gallery');
    if (!gallery) return;
    const heroMock = gallery.querySelector('gallery-wall-frame-mock:not([data-corner-crop])');
    if (!heroMock?.updateConfig) return;
    heroMock.updateConfig({ frameFraction: fraction, frameOffsetY: offsetY });
  }

  #buildCustomPanel() {
    const panel = this.#sheetDialog.querySelector('[data-panel="custom"]');
    if (!panel) return;

    // Read current state from sku-picker
    const sel = this.#skuPicker.state.confirmed;
    this.#customTabState = {
      variantId: this.#skuPicker.currentVariantId,
      framing: sel.framing_option,
      frameColor: sel.frame_color,
      border: sel.border_option,
    };

    const stepLabels = { material: this.#i18n.step_format || 'Format', framing: this.#i18n.step_framing || 'Framing', color: this.#i18n.step_finish || 'Finish', border: this.#i18n.step_border || 'Border' };
    const stepHTML = ['material', 'framing', 'color', 'border'].map(name => `
      <div class="style-picker-custom__step" data-step="${name}">
        <div class="style-picker-custom__step-header">
          <span class="style-picker-custom__step-label">${stepLabels[name]}</span>
        </div>
        <div class="style-picker-custom__step-body"></div>
      </div>
    `).join('');

    panel.innerHTML = `<div class="style-picker-custom">
      ${stepHTML}
      <div class="style-picker-custom__info" hidden></div>
    </div>`;

    this.#renderMaterialStep(panel);
    this.#renderFramingStep(panel);
    this.#renderColorStep(panel);
    this.#renderBorderStep(panel);
    this.#updateCustomPrice();

    this.#customPanelBuilt = true;
  }

  /**
   * Measure the custom panel's current height and lock it as min-height
   * so switching between selections doesn't cause the bottom sheet to jump.
   */
  #lockCustomPanelHeight() {
    const custom = this.#sheetDialog?.querySelector('.style-picker-custom');
    if (!custom) return;
    // Clear lock, measure natural height, then set min-height to the
    // tallest value seen so the sheet never shrinks.
    custom.style.minHeight = '';
    requestAnimationFrame(() => {
      const measured = custom.scrollHeight;
      const prev = this.#customPanelMinHeight || 0;
      const height = Math.max(measured, prev);
      this.#customPanelMinHeight = height;
      custom.style.minHeight = `${height}px`;
    });
  }

  #reinitCustomTabState() {
    const sel = this.#skuPicker.state.confirmed;
    this.#customTabState = {
      variantId: this.#skuPicker.currentVariantId,
      framing: sel.framing_option,
      frameColor: sel.frame_color,
      border: sel.border_option,
    };

    const panel = this.#sheetDialog?.querySelector('[data-panel="custom"]');
    if (!panel) return;

    this.#renderMaterialStep(panel);
    this.#renderFramingStep(panel);
    this.#renderColorStep(panel);
    this.#renderBorderStep(panel);
    this.#updateCustomPrice();
  }

  /* ── Step renderers ── */

  #renderMaterialStep(panel) {
    const step = panel.querySelector('[data-step="material"]');
    if (!step) return;
    const body = step.querySelector('.style-picker-custom__step-body');

    const variants = Object.entries(this.#variantMap);
    // Hide step if single variant
    if (variants.length <= 1) {
      step.classList.add('style-picker-custom__step--hidden');
      if (body) body.innerHTML = '';
      return;
    }
    step.classList.remove('style-picker-custom__step--hidden');

    const currentSize = this.#skuPicker.state.confirmed.size_option;
    const buttons = variants.map(([vid, v]) => {
      const label = v.artProduct === 'canvas-print' ? 'Canvas' : 'Art Print';
      const active = vid === this.#customTabState.variantId ? ' style-picker-custom__segment--active' : '';
      const vc = this.#getVariantComponents(vid);
      const disabled = !vc.size[currentSize] ? ' style-picker-custom__segment--disabled' : '';
      return `<button class="style-picker-custom__segment${active}${disabled}" data-variant-id="${vid}" type="button">${label}</button>`;
    }).join('');

    body.innerHTML = `<div class="style-picker-custom__segmented">${buttons}</div>`;

    step.querySelectorAll('.style-picker-custom__segment').forEach(btn => {
      btn.addEventListener('click', () => {
        if (btn.classList.contains('style-picker-custom__segment--disabled')) return;
        const vid = btn.dataset.variantId;
        if (vid === this.#customTabState.variantId) return;
        this.#customTabState.variantId = vid;

        // Reset framing/color for new variant
        const v = this.#variantMap[vid];
        const firstFraming = v.framingOptions[0];
        this.#customTabState.framing = firstFraming?.handle || 'unframed';
        const newIsUnframed = this.#customTabState.framing === 'unframed' ||
          this.#customTabState.framing.endsWith('-unframed');
        if (newIsUnframed) {
          this.#customTabState.frameColor = null;
        } else {
          this.#customTabState.frameColor = firstFraming?.colors?.[0]?.handle || null;
        }

        // Reset border if no longer valid for the new variant/framing.
        // Canvas never carries a border, and mount validity is framing-
        // dependent — validate the actual (framing, border) fulfillment combo.
        const canvasReset = v.artProduct === 'canvas-print';
        if (canvasReset
            || !this.#isBorderValidForFraming(vid, this.#customTabState.framing, this.#customTabState.border)) {
          this.#customTabState.border = 'no-border';
        }

        this.#previewCustomConfig();
        this.#renderMaterialStep(panel);
        this.#renderFramingStep(panel);
        this.#renderColorStep(panel);
        this.#renderBorderStep(panel);
        this.#autoAdvanceFromStep('material', panel);
      });
    });
  }

  #renderFramingStep(panel) {
    const step = panel.querySelector('[data-step="framing"]');
    if (!step) return;
    const body = step.querySelector('.style-picker-custom__step-body');

    const v = this.#variantMap[this.#customTabState.variantId];
    if (!v) return;

    const currentSize = this.#skuPicker.state.confirmed.size_option;

    // Use variant-specific availability (not the sku-picker's currently-loaded variant)
    const variantComponents = this.#getVariantComponents(this.#customTabState.variantId);

    // Sort: framed options first, "No Frame" last
    const isUnframedHandle = h => h === 'unframed' || h.endsWith('-unframed');
    const framingOptions = [...v.framingOptions].sort((a, b) => {
      if (isUnframedHandle(a.handle)) return 1;
      if (isUnframedHandle(b.handle)) return -1;
      return 0;
    });

    const selectedFraming = this.#customTabState.framing;
    const selectedIsUnframed = isUnframedHandle(selectedFraming);

    // Build tab buttons
    const tabs = framingOptions.map(opt => {
      const isUnframed = isUnframedHandle(opt.handle);
      const active = opt.handle === selectedFraming ? ' style-picker-custom__frame-tab--active' : '';
      const available = isUnframed || !!variantComponents.framing[currentSize]?.[opt.handle];
      const unavailCls = available ? '' : ' style-picker-custom__frame-tab--unavailable';
      const label = isUnframed ? (this.#i18n.no_frame || 'No Frame') : (opt.short_name || opt.label);
      return `<button class="style-picker-custom__frame-tab${active}${unavailCls}" data-framing="${opt.handle}" type="button">${label}</button>`;
    }).join('');

    // Build color swatches for selected framing option
    let contentHTML = '';
    if (selectedIsUnframed) {
      contentHTML = `<div class="style-picker-custom__frame-tab-content">
        <div class="style-picker-custom__frame-tab-placeholder">${this.#i18n.unframed || 'Unframed'}</div>
      </div>`;
    } else {
      const framingOpt = v.framingOptions.find(f => f.handle === selectedFraming);
      const colors = framingOpt?.colors || [];
      if (colors.length) {
        const swatches = colors.map(c => {
          const active = c.handle === this.#customTabState.frameColor ? ' style-picker-custom__swatch--active' : '';
          const hex = c.color_value || COLOR_HEX[c.short_name?.toLowerCase()] || '#333';
          const bgStyle = c.thumbnail
            ? `background-image:url('${c.thumbnail}');background-size:cover;background-repeat:no-repeat;background-position:center;`
            : `background-color: ${hex};`;
          return `<button class="style-picker-custom__swatch${active}" data-color="${c.handle}" type="button">
            <span class="style-picker-custom__swatch-circle" style="${bgStyle}"></span>
            <span class="style-picker-custom__swatch-label">${c.short_name || c.label}</span>
          </button>`;
        }).join('');
        contentHTML = `<div class="style-picker-custom__frame-tab-content">
          <div class="style-picker-custom__swatches">${swatches}</div>
        </div>`;
      }
    }

    body.innerHTML = `<div class="style-picker-custom__frame-tabbed">
      <div class="style-picker-custom__frame-tab-row">${tabs}</div>
      ${contentHTML}
    </div>`;

    // Tab click handler
    step.querySelectorAll('.style-picker-custom__frame-tab').forEach(btn => {
      btn.addEventListener('click', () => {
        if (btn.classList.contains('style-picker-custom__frame-tab--unavailable')) return;
        const handle = btn.dataset.framing;
        if (handle === this.#customTabState.framing) return;

        this.#customTabState.framing = handle;
        const isUnframed = isUnframedHandle(handle);

        if (isUnframed) {
          this.#customTabState.frameColor = null;
        } else {
          const framingOpt = v.framingOptions.find(f => f.handle === handle);
          this.#customTabState.frameColor = framingOpt?.colors?.[0]?.handle || null;
        }

        // Reset border if it isn't valid for the new framing. Mount support
        // is framing-dependent (deep box frames yes, flat classic frames no),
        // so validate the actual (framing, border) fulfillment combo — not
        // just variant/size-level border pricing.
        if (!this.#isBorderValidForFraming(this.#customTabState.variantId, handle, this.#customTabState.border)) {
          this.#customTabState.border = 'no-border';
        }

        this.#previewCustomConfig();
        this.#renderFramingStep(panel);
        this.#renderColorStep(panel);
        this.#renderBorderStep(panel);
        this.#updateAllStepHeaders(panel);
      });
    });

    // Swatch click handler
    step.querySelectorAll('.style-picker-custom__swatch').forEach(btn => {
      btn.addEventListener('click', () => {
        const handle = btn.dataset.color;
        if (handle === this.#customTabState.frameColor) return;
        this.#customTabState.frameColor = handle;
        this.#previewCustomConfig();
        // Re-render swatches only (full framing step re-render)
        this.#renderFramingStep(panel);
        this.#updateAllStepHeaders(panel);
      });
    });

    // Show info when canvas has limited or no availability at the current size
    const infoEl = panel.querySelector('.style-picker-custom__info');
    if (infoEl) {
      const isCanvas = v.artProduct === 'canvas-print';
      const sizeAvailable = !!variantComponents.size?.[currentSize];
      const framedAvailable = framingOptions.some(opt => {
        const isUnfr = isUnframedHandle(opt.handle);
        return !isUnfr && !!variantComponents.framing[currentSize]?.[opt.handle];
      });
      if (isCanvas && !sizeAvailable) {
        infoEl.textContent = this.#i18n.canvas_unavailable || 'Canvas is not available for this size.';
        infoEl.hidden = false;
      } else if (isCanvas && !framedAvailable) {
        infoEl.textContent = this.#i18n.framed_canvas_unavailable || 'Framed Canvas is not available for this size.';
        infoEl.hidden = false;
      } else {
        infoEl.hidden = true;
      }
    }
  }

  #renderColorStep(panel) {
    // Colors are now rendered inside #renderFramingStep() tabbed component
    const step = panel.querySelector('[data-step="color"]');
    if (!step) return;
    step.classList.add('style-picker-custom__step--hidden');
    const body = step.querySelector('.style-picker-custom__step-body');
    if (body) body.innerHTML = '';
  }

  #renderBorderStep(panel) {
    const step = panel.querySelector('[data-step="border"]');
    if (!step) return;
    const body = step.querySelector('.style-picker-custom__step-body');

    const v = this.#variantMap[this.#customTabState.variantId];
    const variantConfig = this.#skuPicker.variantConfigurators.variants[this.#customTabState.variantId];
    const borderOptions = variantConfig?.options?.border;
    if (!borderOptions?.length) {
      step.classList.add('style-picker-custom__step--hidden');
      if (body) body.innerHTML = '';
      return;
    }
    if (v?.artProduct === 'canvas-print') {
      step.classList.add('style-picker-custom__step--hidden');
      if (body) body.innerHTML = '';
      return;
    }
    step.classList.remove('style-picker-custom__step--hidden');

    const currentSize = this.#skuPicker.state.confirmed.size_option;

    // Use variant-specific availability (not the sku-picker's currently-loaded variant)
    const variantComponents = this.#getVariantComponents(this.#customTabState.variantId);

    // In-place update if same border options exist
    const existingBorderBtns = body.querySelectorAll('.style-picker-custom__swatch[data-border]');
    const existingBorderHandles = [...existingBorderBtns].map(b => b.dataset.border);
    const newBorderHandles = borderOptions.map(o => o.handle);
    const canPatchBorders = existingBorderBtns.length > 0
      && existingBorderHandles.length === newBorderHandles.length
      && existingBorderHandles.every((h, i) => h === newBorderHandles[i]);

    if (canPatchBorders) {
      existingBorderBtns.forEach(btn => {
        const handle = btn.dataset.border;
        const isActive = handle === this.#customTabState.border;
        const comboInvalid = !this.#isBorderValidForFraming(this.#customTabState.variantId, this.#customTabState.framing, handle);
        const sizeUnavailable = !variantComponents.border[currentSize]?.[handle];
        btn.classList.toggle('style-picker-custom__swatch--active', isActive);
        btn.classList.toggle('style-picker-custom__swatch--disabled', comboInvalid || sizeUnavailable);
      });
      return;
    }

    const swatches = borderOptions.map(opt => {
      const active = opt.handle === this.#customTabState.border ? ' style-picker-custom__swatch--active' : '';
      const comboInvalid = !this.#isBorderValidForFraming(this.#customTabState.variantId, this.#customTabState.framing, opt.handle);
      const sizeUnavailable = !variantComponents.border[currentSize]?.[opt.handle];
      const disabled = comboInvalid || sizeUnavailable ? ' style-picker-custom__swatch--disabled' : '';
      return `<button class="style-picker-custom__swatch${active}${disabled}" data-border="${opt.handle}" type="button">
        <span class="style-picker-custom__swatch-circle" style="background-image:url('${opt.thumbnail}');background-size:cover;background-repeat:no-repeat;background-position:center;"></span>
        <span class="style-picker-custom__swatch-label">${opt.label}</span>
      </button>`;
    }).join('');

    body.innerHTML = `<div class="style-picker-custom__swatches">${swatches}</div>`;

    step.querySelectorAll('.style-picker-custom__swatch').forEach(btn => {
      btn.addEventListener('click', () => {
        if (btn.classList.contains('style-picker-custom__swatch--disabled')) return;
        const handle = btn.dataset.border;
        if (handle === this.#customTabState.border) return;
        this.#customTabState.border = handle;
        this.#previewCustomConfig();
        this.#renderBorderStep(panel);
        this.#autoAdvanceFromStep('border', panel);
      });
    });
  }

  /* ── Accordion helpers ── */

  #getStepSelectionLabel(stepName) {
    const state = this.#customTabState;
    switch (stepName) {
      case 'material': {
        const v = this.#variantMap[state.variantId];
        return v?.artProduct === 'canvas-print' ? 'Canvas' : 'Art Print';
      }
      case 'framing': {
        const v = this.#variantMap[state.variantId];
        const opt = v?.framingOptions.find(f => f.handle === state.framing);
        return opt?.short_name || opt?.label || '';
      }
      case 'color': {
        const v = this.#variantMap[state.variantId];
        const framingOpt = v?.framingOptions.find(f => f.handle === state.framing);
        const color = framingOpt?.colors?.find(c => c.handle === state.frameColor);
        return color?.short_name || color?.label || '';
      }
      case 'border': {
        const variantConfig = this.#skuPicker.variantConfigurators.variants[state.variantId];
        const borderOptions = variantConfig?.options?.border;
        const opt = borderOptions?.find(b => b.handle === state.border);
        return opt?.label || '';
      }
      default: return '';
    }
  }

  #updateAllStepHeaders(panel) {
    panel.querySelectorAll('.style-picker-custom__step').forEach(step => {
      const stepName = step.dataset.step;
      const valueEl = step.querySelector('.style-picker-custom__step-value');
      if (valueEl) valueEl.textContent = this.#getStepSelectionLabel(stepName);
    });
  }

  #toggleAccordionStep(_stepName) {
    // No-op: accordion removed in favour of flat layout
  }

  #syncAccordionState(_panel) {
    // No-op: accordion removed in favour of flat layout
  }

  #getFirstVisibleStep() {
    const panel = this.#sheetDialog?.querySelector('[data-panel="custom"]');
    if (!panel) return null;
    for (const step of panel.querySelectorAll('.style-picker-custom__step')) {
      if (!step.classList.contains('style-picker-custom__step--hidden')) return step.dataset.step;
    }
    return null;
  }

  #getNextVisibleStep(current) {
    const panel = this.#sheetDialog?.querySelector('[data-panel="custom"]');
    if (!panel) return null;
    const steps = [...panel.querySelectorAll('.style-picker-custom__step')];
    const currentIdx = steps.findIndex(s => s.dataset.step === current);
    for (let i = currentIdx + 1; i < steps.length; i++) {
      if (!steps[i].classList.contains('style-picker-custom__step--hidden')) return steps[i].dataset.step;
    }
    return null;
  }

  #autoAdvanceFromStep(_current, panel) {
    this.#updateAllStepHeaders(panel);
  }

  /* ── Custom config preview (hero mock only) ── */

  #previewCustomConfig() {
    const state = this.#customTabState;
    const v = this.#variantMap[state.variantId];
    const framingOpt = v?.framingOptions.find(f => f.handle === state.framing);
    const color = framingOpt?.colors?.find(c => c.handle === state.frameColor);
    const hex = color?.color_value || COLOR_HEX[color?.short_name?.toLowerCase()] || '#333';
    const isUnframed = state.framing === 'unframed' || state.framing?.endsWith('-unframed');

    const config = {
      frameColor: hex,
      mouldingTile: isUnframed ? '' : (color?.moulding_tile || ''),
      mouldingWidth: isUnframed ? 0 : (color?.moulding_width || null),
      hasFrame: !isUnframed,
      borderOption: state.border,
      borderWidth: this.#borderWidthCm(state.border),
    };

    if (v?.artProduct) {
      config.productType = v.artProduct === 'canvas-print' ? 'canvas' : 'art_print';
    }

    // Update the hero frame mock in the media gallery
    const gallery = document.querySelector('media-gallery') || document.querySelector('gallery-wall-media-gallery');
    if (gallery) {
      const heroMock = gallery.querySelector('gallery-wall-frame-mock');
      if (heroMock?.updateConfig) heroMock.updateConfig(config);
    }

    // Broadcast to room mock so lifestyle preview stays in sync
    document.dispatchEvent(new CustomEvent('gallery-wall:config-changed', {
      bubbles: true,
      detail: config,
    }));

    this.#switchToFrameSlide();
    this.#updateCustomPrice();
  }

  /* ── Custom config application (full broadcast) ── */

  #applyCustomConfig() {
    const sp = this.#skuPicker;
    const state = this.#customTabState;
    const needsVariantSwitch = state.variantId !== sp.currentVariantId;

    if (needsVariantSwitch) {
      sp.currentVariantId = state.variantId;
      sp._loadVariantConfigurator(state.variantId);

      sp.container.dispatchEvent(new CustomEvent('sku-picker:variant-changed', {
        bubbles: true,
        detail: { variantId: state.variantId },
      }));

      const radio = document.querySelector(
        `variant-picker input[data-variant-id="${state.variantId}"]`
      );
      if (radio && !radio.checked) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      }

      this.#hideSkuPickerRows();
    }

    // Sync state
    sp.state.confirmed.framing_option = state.framing;
    sp.state.confirmed.frame_color = state.frameColor;
    sp.state.confirmed.border_option = state.border;

    // Commit-point guard: border validity is framing-dependent (a mount needs
    // a deep box frame). Drop an invalid border even if the custom-tab state
    // failed to clear it, so an invalid (framing, border) combo never reaches
    // the cart. Also covers unframed, which never carries a border.
    if (!this.#isBorderValidForFraming(state.variantId, state.framing, sp.state.confirmed.border_option)) {
      sp.state.confirmed.border_option = 'no-border';
      state.border = 'no-border';
    }

    // Pre-resolve active style name before _updateConfirmedSelection so that
    // _updateFormProperties picks up the correct name. #syncRowAfterCustomChange
    // runs after _updateConfirmedSelection and is too late for form property building.
    const preMatch = this.#presets.find(p =>
      p.resolvedVariantId === state.variantId &&
      p.resolvedFraming === sp.state.confirmed.framing_option &&
      p.frameColorHandle === sp.state.confirmed.frame_color &&
      p.border === sp.state.confirmed.border_option
    );
    sp.activeStyleName = preMatch ? (preMatch.name || null) : null;

    sp._updateConfirmedSelection(true);

    // Switch to frame mock slide
    this.#switchToFrameSlide();

    // Update price
    this.#updateCustomPrice();

    // Sync the inline card row
    this.#syncRowAfterCustomChange();

    const v = this.#variantMap[state.variantId];
    document.dispatchEvent(new CustomEvent('style-picker:custom-applied', {
      detail: {
        variantId: state.variantId,
        framing: state.framing,
        frameColor: state.frameColor,
        border: state.border,
        productType: v?.artProduct === 'canvas-print' ? 'canvas' : (v?.artProduct ? 'art_print' : null),
      }
    }));
  }

  #updateCustomPrice() {
    const footer = this.#sheetDialog?.querySelector('.style-picker-sheet__footer');
    if (!footer) return;
    const priceEl = footer.querySelector('.style-picker-sheet__price-value');
    if (!priceEl) return;

    const sp = this.#skuPicker;
    const state = this.#customTabState;
    const sizeOption = sp.state.confirmed.size_option;

    // Use variant-specific components so pricing works regardless of which variant the sku-picker has loaded
    const vc = this.#getVariantComponents(state.variantId);
    const sizeComp = vc.size[sizeOption];
    let framingComp = vc.framing[sizeOption]?.[state.framing];
    const borderComp = vc.border[sizeOption]?.[state.border];

    // Unframed is always available at price 0
    if ((state.framing === 'unframed' || state.framing?.endsWith('-unframed')) && !framingComp) {
      framingComp = { price: 0, compare_at_price: 0 };
    }

    if (!sizeComp || !framingComp || !borderComp) {
      priceEl.textContent = '';
      return;
    }

    const total = (sizeComp.price || 0) + (framingComp.price || 0) + (borderComp.price || 0);
    if (total <= 0) { priceEl.textContent = ''; return; }

    const formatted = sp._formatPrice(total);
    const compareAt = (sizeComp.compare_at_price || 0) + (framingComp.compare_at_price || 0) + (borderComp.compare_at_price || 0);
    if (compareAt > total) {
      priceEl.innerHTML = `<span class="style-picker-sheet__price-compare">${sp._formatPrice(compareAt)}</span> ${formatted}`;
    } else {
      priceEl.textContent = formatted;
    }
  }

  #syncRowAfterCustomChange() {
    const state = this.#customTabState;

    // Always show the Custom card when the user has explicitly used the Custom tab —
    // even if the config happens to match an existing row preset. The user's intent
    // was to build a custom frame, so "Custom" must appear at position 1.
    const allMatch = this.#allPresets.find(p =>
      p.resolvedVariantId === state.variantId &&
      p.resolvedFraming === state.framing &&
      p.frameColorHandle === state.frameColor &&
      p.border === state.border
    );

    this.#hasUserCustomSelection = true;
    this.#preferredStyleId = allMatch?.id || null;
    this.#showCustomCard(null, { isCustomBuilt: true });
    this.#updateRowCards();
  }
}

// Guard: if URL param explicitly disables the style picker, don't register
// the custom element. This prevents re-activation if a section render morphs
// a <style-picker> element back into the DOM without the query param context.
if (new URLSearchParams(window.location.search).get('pdp_styles') !== '0') {
  if (!customElements.get('style-picker')) {
    customElements.define('style-picker', StylePicker);
  }
}
